// Corporate Health portal.
//
// Ports the corporate-portal HTML prototype to React while reusing
// our shared portal scaffold (PortalLayout, SidebarNav, scoped
// MedicalPortalSans font, animated CorporateBrand). The HTML's
// Archivo/Spectral/Bebas Neue stack is replaced with our portal
// font; the HTML's forest/gold palette is preserved via expanded
// design tokens. No login/role switching — the existing auth flow
// owns sign-in; this file only renders portal sections.
//
// Ten sections in three groups:
//   Workspace  — overview, analytics, hps, rounds
//   Compliance — statutory, alerts, esg, inspection, reports
//   Admin      — settings
//
// Two-year demo dataset (FY 2025-26 + FY 2024-25). Topbar carries
// the FY + Round selectors. Three PDF report flows (HR workforce,
// statutory compliance, ESG/BRSR) wire to jsPDF + autoTable.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import MonitorHeartOutlinedIcon from '@mui/icons-material/MonitorHeartOutlined';
import LayersOutlinedIcon from '@mui/icons-material/LayersOutlined';
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import EnergySavingsLeafOutlinedIcon from '@mui/icons-material/EnergySavingsLeafOutlined';
import WorkspacePremiumOutlinedIcon from '@mui/icons-material/WorkspacePremiumOutlined';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowOutwardIcon from '@mui/icons-material/ArrowOutward';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import PortalLayout from '../../layouts/PortalLayout';
import { SidebarLabel, SidebarNavItem, SidebarUser } from '../../layouts/SidebarNav';
import Logo from '../../components/common/Logo';
import logoSrc from '../../assets/img/trustlab-logo.png';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import Workforce from './corporate/Workforce';
import { useAuth } from '../../context/AuthContext';
import { ROUTES } from '../../constants/routes';
import {
  useGetCorpSettingsQuery,
  useUpdateCorpSettingsMutation,
  useListStatutoryQuery,
  useCreateStatutoryMutation,
  useDeleteStatutoryMutation,
  useListCapaQuery,
  useCreateCapaMutation,
  useDeleteCapaMutation,
  useListInspectionsQuery,
  useCreateInspectionMutation,
  useDeleteInspectionMutation,
  useListRoundsQuery,
  useCreateRoundMutation,
  useDeleteRoundMutation,
  useListCertificatesQuery,
  useCreateCertificateMutation,
  useDeleteCertificateMutation,
  useGetWorkforceAggregateQuery,
} from '../../store/corporateApi';

// ─── Scoped portal font (shared with lab + medical portals) ─────
const FONT_TAG  = 'data-medical-portal-font';
const RULE_TAG  = 'data-medical-portal-rule';
const BODY_CLASS = 'medical-portal-active';

const loadPortalFont = () => {
  if (!document.querySelector(`style[${FONT_TAG}]`)) {
    fetch('https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@400;500;600;700&family=IBM+Plex+Mono:wght@400;500;600;700&display=swap')
      .then((r) => (r.ok ? r.text() : Promise.reject()))
      .then((css) => {
        // Two families, two private names. Both re-declarations have to
        // happen in this one stylesheet: Google returns them interleaved in
        // a single response, so splitting the fetch would double the
        // round-trips for no gain.
        const scoped = css
          .replace(/font-family:\s*'IBM Plex Sans'/g, "font-family: 'MedicalPortalSans'")
          .replace(/font-family:\s*'IBM Plex Mono'/g, "font-family: 'MedicalPortalMono'");
        const style = document.createElement('style');
        style.setAttribute(FONT_TAG, '');
        style.textContent = scoped;
        document.head.appendChild(style);
      })
      .catch(() => { /* font fetch failed; portal falls back to sans-serif */ });
  }

  if (!document.querySelector(`style[${RULE_TAG}]`)) {
    const rule = document.createElement('style');
    rule.setAttribute(RULE_TAG, '');
    // EVERY SELECTOR IS WRAPPED IN `:where()`, WHICH IS THE POINT.
    //
    // `!important` is unavoidable here — it is the only thing that beats
    // MUI's own component font-family on the portalled surfaces. But an
    // `!important` on a selector like `body.x *` also beats every sx prop
    // in the portal, which silently flattened all 179 `fontFamily: MONO`
    // sites to sans. `:where()` contributes zero specificity, so this rule
    // still wins against MUI (which sets no `!important`) while LOSING to
    // the `!important` baked into MONO in medical/shared.js. That one-sided
    // outcome is what keeps table headers, IDs and figures monospaced.
    rule.textContent = `
      :where(body.${BODY_CLASS}),
      :where(body.${BODY_CLASS}) *,
      :where(body.${BODY_CLASS} .MuiPopover-root),
      :where(body.${BODY_CLASS} .MuiModal-root),
      :where(body.${BODY_CLASS} .MuiDrawer-root),
      :where(body.${BODY_CLASS} .MuiTooltip-popper),
      :where(body.${BODY_CLASS} .MuiMenu-root),
      :where(body.${BODY_CLASS} .MuiPopover-root) *,
      :where(body.${BODY_CLASS} .MuiModal-root) *,
      :where(body.${BODY_CLASS} .MuiDrawer-root) *,
      :where(body.${BODY_CLASS} .MuiTooltip-popper) *,
      :where(body.${BODY_CLASS} .MuiMenu-root) * {
        font-family: 'MedicalPortalSans', system-ui, sans-serif !important;
      }

      /* NO RUBBER-BAND. Dragging past the top or bottom of the portal used
         to bounce the whole page, which on a dense register reads as the
         table coming loose.

         Two rules, deliberately separate. The first is the one that does
         the work: overscroll-behavior set on BODY propagates to the
         viewport when the root element leaves its own value at 'auto',
         which is the case here. The second says the same thing on the root
         for engines that want it there - split out because :has() is the
         newer feature of the two, and an unsupported selector invalidates
         only its own rule, so the body rule survives either way.

         Both are scoped to the portal's body class, which the dashboard
         removes on unmount, so the rest of the site scrolls as it always
         has. */
      body.${BODY_CLASS} { overscroll-behavior: none; }
      html:has(> body.${BODY_CLASS}) { overscroll-behavior: none; }

      /* And a scroller inside the portal — a long table, the sidebar —
         keeps its overscroll to itself instead of handing it to the page
         once it hits its own end. */
      body.${BODY_CLASS} [class*='MuiDrawer-paper'],
      body.${BODY_CLASS} [class*='MuiDialog-paper'],
      body.${BODY_CLASS} [class*='MuiDialogContent-root'] {
        overscroll-behavior: contain;
      }

      /* LOADING PLACEHOLDERS.
         MUI's "wave" variant sweeps a white gradient across each bar. With
         a dozen bars on screen that is a dozen highlights travelling at
         once, which draws the eye to the waiting rather than to the page.
         The bars are now AIMS's own --rule-2, pulsing slowly in place:
         present enough to say "this is coming", quiet enough to ignore. */
      body.${BODY_CLASS} .MuiSkeleton-root {
        background-color: #EDF1F4;
      }
      /* Belt and braces - the sweep is drawn by a pseudo-element, so a
         stray animation="wave" anywhere still renders as a calm pulse. */
      body.${BODY_CLASS} .MuiSkeleton-wave::after {
        display: none;
      }
      body.${BODY_CLASS} .MuiSkeleton-wave {
        animation: mp-skeleton-pulse 1.6s ease-in-out .5s infinite;
      }
      @keyframes mp-skeleton-pulse {
        0%, 100% { opacity: 1; }
        50%      { opacity: .45; }
      }
      /* Someone who has asked their system for less motion gets none. */
      @media (prefers-reduced-motion: reduce) {
        body.${BODY_CLASS} .MuiSkeleton-root {
          animation: none !important;
          opacity: .75;
        }
      }
    `;
    document.head.appendChild(rule);
  }
};

// ─── Today's date (matches lab/medical topbar) ──────────────────
const TODAY_LABEL = (() => {
  const d = new Date();
  const wk = d.toLocaleDateString('en-GB', { weekday: 'short' });
  const dy = d.toLocaleDateString('en-GB', { day: '2-digit' });
  const mo = d.toLocaleDateString('en-GB', { month: 'short' });
  const yr = d.getFullYear();
  return `${wk}, ${dy} ${mo}, ${yr}`;
})();

// ─── Design tokens (corporate-portal.html) ──────────────────────
// forest/gold palette transplanted from the prototype's CSS vars
// so colour parity with the design is preserved.
const C = {
  forest:   '#1A3A2A',
  forestD:  '#0F2419',
  forestL:  '#2A5238',
  forestXL: '#3D7A52',
  gold:     '#C8A24B',
  goldL:    '#E8D9A8',
  goldD:    '#a8843a',
  ink:      '#19211d',
  slate:    '#5a6862',
  slateL:   '#8a958f',
  line:     '#e2e7e3',
  lineD:    '#cfd6d1',
  paper:    '#ffffff',
  wash:     '#f5f8f6',
  wash2:    '#eef3ef',
  bg:       '#e9efe9',
  ok:       '#1f7a4d',
  okBg:     '#e7f3ec',
  warn:     '#9a6b00',
  warnBg:   '#fbf2da',
  due:      '#a23b2e',
  dueBg:    '#f7e6e3',
  blue:     '#2e6b8f',
  blueBg:   '#e6eff5',
};

const RISK_COLORS = {
  Optimal:  '#1f7a4d',
  Low:      '#6aae80',
  Moderate: '#C8A24B',
  High:     '#d98032',
  Critical: '#a23b2e',
};

const CLIENT_DEFAULTS = {
  name:  'Acme Industries Pvt Ltd',
  id:    'ACM-HYD-0142',
  sites: 11,
  ohc:   1,
};

// ─── Demo dataset — two financial years ─────────────────────────
const DATA = {
  'FY 2025-26': {
    workforce: 3200, eligible: 3032, screened: 2847, participation: 94,
    complianceScore: 87, highRisk: 342, criticalRefer: 86, openItems: 17,
    lastInspection: '12 Apr 2026', lastESIC: '28 Feb 2026', inspectionResult: 'Cleared',
    risk: [['Optimal', 1167, 41], ['Low', 797, 28], ['Moderate', 541, 19], ['High', 256, 9], ['Critical', 86, 3]],
    biomarkers: [
      ['Vitamin D deficiency', 58], ['HbA1c ≥ 5.7% (pre-diabetic+)', 31], ['Dyslipidaemia (LDL/TG)', 29],
      ['Vitamin B12 deficiency', 22], ['Elevated blood pressure', 24], ['Obesity (BMI ≥ 30)', 19],
      ['Anaemia (low Hb)', 17], ['Elevated liver enzymes', 14], ['Elevated uric acid', 13], ['TSH abnormal', 11],
    ],
    departments: [
      ['Production / Shop Floor', 1120, 34], ['Logistics / Warehouse', 480, 29], ['Maintenance / Engg.', 360, 31],
      ['Sales / Field', 260, 33], ['Admin / Finance', 320, 26], ['Quality / Lab', 210, 22], ['Management', 97, 28],
    ],
    programmes: [
      ['Annual Health Checkup (AHC)', 3032, 2847, 94, 19], ['Statutory PME (hazardous)', 612, 580, 95, 20],
      ['Audiometry Surveillance', 520, 504, 97, 12], ['Spirometry Surveillance', 86, 86, 100, 12],
      ['Pre-Employment Medicals', 136, 124, 91, 18], ['Executive Health Programme', 48, 48, 100, 24],
      ["Drivers' Fitness (Form 1A)", 47, 47, 100, 8], ['Food-Handler Fitness (FSSAI)', 18, 15, 83, 24],
    ],
    statutory: [
      ['Factories Act 1948 — Periodic Medical (hazardous)', '§41C(b) · Form 17/33', 612, 580, 'w', '32 in-window', '12 Apr 2026'],
      ['Factories Act / ESI — Pre-Employment Medical', '§41C(a) · ESI Form 1', 136, 124, 'd', '12 pending', '30 Apr 2026'],
      ["Motor Vehicles — Drivers' Fitness", 'CMV Rules 1989, Form 1A', 47, 47, 'ok', 'All clear', '12 Apr 2026'],
      ['FSSAI — Food-Handler Fitness', 'FSS (L&R) Regs 2011, Sch.4', 18, 15, 'w', '3 renewing', '03 Oct 2025'],
      ['Audiometric Surveillance — noise', 'ISO 1999:2013', 520, 504, 'ok', 'Complete', 'Q1 FY26'],
      ['Spirometric Surveillance — dust', 'ATS/ERS 2019', 86, 86, 'ok', 'Complete', 'Q1 FY26'],
      ['Vaccination — Hep-B / TT', 'Occupier immunisation policy', 124, 124, 'ok', 'Compliant', 'Tier-1 done'],
      ['Maternity Benefit — return-to-work', 'Maternity Benefit Act 1961', 4, 2, 'w', '2 in-window', 'Within window'],
    ],
    capa: [
      ['New-joiner PEMs pending submission to ESIC', 12, 'Within 14 days of joining', 'HR Lead', 'd'],
      ['Periodic medicals due (hazardous), in 12-month cycle', 32, 'Before cycle expiry', 'OHC i/c', 'w'],
      ['Food-handler fitness certificates due for renewal', 3, 'By next service date', 'Admin', 'w'],
      ['Return-from-maternity-leave medicals', 2, 'Within return window', 'HR Lead', 'w'],
      ['Audiometry — workers on leave during camp', 16, 'Next camp cycle', 'OHC i/c', 'w'],
    ],
    rounds: [
      ['R1', 'Annual Health Checkup — Round 1', '15 Apr – 30 May 2025', 'All sites · 8 camps', 2847, 94, 'AHC full panel · 66 parameters'],
      ['R2', 'Statutory PME & Surveillance Camp', '08 – 26 Sep 2025', 'Shop floor + hazardous units', 1170, 96, 'Form 17 PME · Audiometry · Spirometry'],
      ['R3', 'Q4 New-Joiner & Executive Cohort', '20 Jan – 18 Feb 2026', 'HQ + regional offices', 172, 100, 'PEM · Executive Health Programme'],
    ],
  },
  'FY 2024-25': {
    workforce: 2980, eligible: 2950, screened: 2624, participation: 89,
    complianceScore: 81, highRisk: 332, criticalRefer: 104, openItems: 9,
    lastInspection: '19 Mar 2025', lastESIC: '11 Dec 2024', inspectionResult: 'Cleared',
    risk: [['Optimal', 997, 38], ['Low', 708, 27], ['Moderate', 525, 20], ['High', 289, 11], ['Critical', 105, 4]],
    biomarkers: [
      ['Vitamin D deficiency', 62], ['HbA1c ≥ 5.7% (pre-diabetic+)', 34], ['Dyslipidaemia (LDL/TG)', 31],
      ['Vitamin B12 deficiency', 25], ['Elevated blood pressure', 26], ['Obesity (BMI ≥ 30)', 20],
      ['Anaemia (low Hb)', 18], ['Elevated liver enzymes', 15], ['Elevated uric acid', 14], ['TSH abnormal', 12],
    ],
    departments: [
      ['Production / Shop Floor', 1040, 37], ['Logistics / Warehouse', 440, 31], ['Maintenance / Engg.', 330, 33],
      ['Sales / Field', 240, 35], ['Admin / Finance', 300, 28], ['Quality / Lab', 184, 24], ['Management', 90, 30],
    ],
    programmes: [
      ['Annual Health Checkup (AHC)', 2950, 2624, 89, 26], ['Statutory PME (hazardous)', 590, 560, 95, 24],
      ['Audiometry Surveillance', 500, 486, 97, 15], ['Spirometry Surveillance', 80, 80, 100, 15],
      ['Pre-Employment Medicals', 120, 118, 98, 22], ['Executive Health Programme', 42, 42, 100, 28],
      ["Drivers' Fitness (Form 1A)", 44, 44, 100, 10], ['Food-Handler Fitness (FSSAI)', 16, 16, 100, 26],
    ],
    statutory: [
      ['Factories Act 1948 — Periodic Medical (hazardous)', '§41C(b) · Form 17/33', 590, 560, 'w', '30 in-window', '19 Mar 2025'],
      ['Factories Act / ESI — Pre-Employment Medical', '§41C(a) · ESI Form 1', 120, 118, 'ok', 'Compliant', '28 Feb 2025'],
      ["Motor Vehicles — Drivers' Fitness", 'CMV Rules 1989, Form 1A', 44, 44, 'ok', 'All clear', '19 Mar 2025'],
      ['FSSAI — Food-Handler Fitness', 'FSS (L&R) Regs 2011, Sch.4', 16, 16, 'ok', 'Compliant', '02 Oct 2024'],
      ['Audiometric Surveillance — noise', 'ISO 1999:2013', 500, 486, 'ok', 'Complete', 'Q2 FY25'],
      ['Spirometric Surveillance — dust', 'ATS/ERS 2019', 80, 80, 'ok', 'Complete', 'Q2 FY25'],
      ['Vaccination — Hep-B / TT', 'Occupier immunisation policy', 118, 118, 'ok', 'Compliant', 'Tier-1 done'],
      ['Maternity Benefit — return-to-work', 'Maternity Benefit Act 1961', 3, 3, 'ok', 'Compliant', 'Closed'],
    ],
    capa: [
      ['Periodic medicals due (hazardous), in 12-month cycle', 30, 'Before cycle expiry', 'OHC i/c', 'w'],
      ['Audiometry — workers on leave during camp', 14, 'Next camp cycle', 'OHC i/c', 'w'],
    ],
    rounds: [
      ['R1', 'Annual Health Checkup — Round 1', '06 May – 28 Jun 2024', 'All sites · 7 camps', 2624, 89, 'AHC full panel · 64 parameters'],
      ['R2', 'Statutory PME & Surveillance Camp', '14 – 30 Oct 2024', 'Shop floor + hazardous units', 1126, 96, 'Form 17 PME · Audiometry · Spirometry'],
    ],
  },
};

// Predictive / forecasting dataset paired to each FY (HPS pillars
// + projected disease progression cohorts).
const EXT = {
  'FY 2025-26': {
    hps: {
      pillars: [
        ['Biomarkers', '50% weight · status·stability·velocity', 55],
        ['Lifestyle', '30% weight', 56],
        ['Medication adherence', '20% weight', 68],
      ],
      prevPillars: [50, 54, 64],
      dist: [
        ['Excellent', '800–900', 7], ['Very Good', '700–799', 24],
        ['Good', '600–699', 38], ['Fair', '500–599', 22], ['Poor', '300–499', 9],
      ],
      byDept: [
        ['Production / Shop Floor', 612], ['Logistics / Warehouse', 631],
        ['Maintenance / Engg.', 620], ['Sales / Field', 626],
        ['Admin / Finance', 668], ['Quality / Lab', 681], ['Management', 655],
      ],
    },
    forecast: [
      ['Type 2 Diabetes', 'HbA1c 5.7–6.4 (pre-diabetic)', 882, '18 months', 123, 44, 79, '₹ 36.5 L'],
      ['Stage-2 Hypertension', 'Elevated / Stage-1 BP', 683, '24 months', 61, 28, 33, '₹ 9.9 L'],
      ['Chronic Kidney Disease (early)', 'Reduced eGFR / microalbuminuria', 38, '24 months', 12, 5, 7, '₹ 8.4 L'],
      ['Cardiovascular event (10-yr)', 'Elevated ASCVD risk band', 156, '5 years', 19, 11, 8, '₹ 14.0 L'],
    ],
    forecastCost: '₹ 68.8 lakh', forecastAvoid: 127,
  },
  'FY 2024-25': {
    hps: {
      pillars: [
        ['Biomarkers', '50% weight · status·stability·velocity', 50],
        ['Lifestyle', '30% weight', 54],
        ['Medication adherence', '20% weight', 64],
      ],
      prevPillars: [47, 52, 61],
      dist: [
        ['Excellent', '800–900', 6], ['Very Good', '700–799', 21],
        ['Good', '600–699', 37], ['Fair', '500–599', 24], ['Poor', '300–499', 12],
      ],
      byDept: [
        ['Production / Shop Floor', 598], ['Logistics / Warehouse', 612],
        ['Maintenance / Engg.', 604], ['Sales / Field', 609],
        ['Admin / Finance', 651], ['Quality / Lab', 666], ['Management', 640],
      ],
    },
    forecast: [
      ['Type 2 Diabetes', 'HbA1c 5.7–6.4 (pre-diabetic)', 893, '18 months', 132, 49, 83, '₹ 38.4 L'],
      ['Stage-2 Hypertension', 'Elevated / Stage-1 BP', 682, '24 months', 64, 30, 34, '₹ 10.2 L'],
      ['Chronic Kidney Disease (early)', 'Reduced eGFR / microalbuminuria', 41, '24 months', 13, 6, 7, '₹ 8.4 L'],
      ['Cardiovascular event (10-yr)', 'Elevated ASCVD risk band', 168, '5 years', 22, 13, 9, '₹ 15.8 L'],
    ],
    forecastCost: '₹ 72.8 lakh', forecastAvoid: 133,
  },
};

// Full statute reference map — every Indian mandate an inspector
// can test, paired with applicability + how it is discharged.
const MANDATE_MAP = [
  ['Factories Act, 1948', '§41C & Schedule II · Form 17, Form 33',
    'Pre-employment & periodic (≤12-month) medical examination of workers in hazardous processes; records preserved.',
    'Applicable',
    'PME/PEM panels delivered; results entered in Form 17 Health Register; Form 33 Certificate of Fitness countersigned by Certifying Surgeon.'],
  ['ESI Act, 1948', 'Insured-person medicals · Form 1',
    'Pre-employment & statutory medicals for ESI-eligible workers; declaration filing to ESIC.',
    'Applicable',
    'PEM masters maintained with IP numbers; submissions tracked against the ESIC portal.'],
  ['Motor Vehicles Act / CMV Rules, 1989', 'Driver fitness · Form 1A',
    'Vision & general medical fitness for workers engaged in driving duties.',
    'Applicable',
    'Form 1A vision + general fitness master with per-driver expiry tracking.'],
  ['FSS (L&R) Regulations, 2011', 'Schedule 4, Part II',
    'Annual medical fitness certification of all food handlers.',
    'Applicable',
    'Food-handler fitness panel incl. infectious-disease screen, issued per canteen/pantry staff.'],
  ['Maternity Benefit Act, 1961', 'Return-to-work fitness',
    'Medical assessment on return from maternity leave where required.',
    'Applicable',
    'Return-from-leave medicals scheduled within the permissible window.'],
  ['Bio-Medical Waste Mgmt Rules, 2016', 'Authorisation & manifests',
    'Safe handling and disposal of biomedical waste generated at on-site camps.',
    'Service-side',
    'TrustLab operates under valid BMW authorisation; camp waste returned through the authorised chain; manifests retained.'],
  ['Mines Act, 1952 / DGMS', 'Mines Rules 1955, r.29B · Form O',
    'Initial & periodical medical examination of persons employed in mines.',
    'Not applicable',
    'No mining/quarrying operations at the establishment for the period. Listed for completeness.'],
  ['BOCW Act, 1996', 'Periodic medical of construction workers',
    'Health & safety medicals for building/construction workers.',
    'Not applicable',
    'No notified construction workforce engaged for the period.'],
  ['DPDP Act, 2023', 'Health-data processing',
    'Lawful, consent-based processing and protection of employee health data.',
    'Applicable',
    'Segregated consent; encryption in transit & at rest; access-controlled individual records; aggregate sharing only.'],
];

// Mandate registry — applicability toggles per corporate client.
const MANDATES = [
  ['factories_pme', 'Factories Act §41C — Periodic Medical (hazardous)', 'Form 17 / Form 33'],
  ['pem',           'Pre-Employment Medical (Factories / ESI)',           'ESI Act 1948 · Form 1'],
  ['drivers',       "Drivers' Fitness Certification",                     'CMV Rules 1989 · Form 1A'],
  ['fssai',         'Food-Handler Fitness',                               'FSS (L&R) Regs 2011, Sch. 4'],
  ['maternity',     'Return-from-maternity Medical',                      'Maternity Benefit Act 1961'],
  ['audio',         'Audiometric Surveillance (noise)',                   'ISO 1999:2013'],
  ['spiro',         'Spirometric Surveillance (dust)',                    'ATS/ERS 2019'],
  ['vacc',          'Vaccination (Hep-B / TT)',                           'Occupier immunisation policy'],
  ['mines',         'Mines Medical Examination',                          'Mines Act 1952 · Form O (DGMS)'],
  ['bocw',          'Construction-Worker Medical',                        'BOCW Act 1996'],
  ['bmw',           'Bio-Medical Waste handling',                         'BMW Mgmt Rules 2016'],
  ['dpdp',          'Health-data protection',                             'DPDP Act 2023'],
];

const MANDATE_DEFAULT = {
  factories_pme: true, pem: true, drivers: true, fssai: true, maternity: true,
  audio: true, spiro: true, vacc: true, mines: false, bocw: false, bmw: true, dpdp: true,
};

const PRESETS = {
  manufacturing: { factories_pme: 1, pem: 1, drivers: 1, fssai: 1, maternity: 1, audio: 1, spiro: 1, vacc: 1, mines: 0, bocw: 0 },
  logistics:     { factories_pme: 0, pem: 1, drivers: 1, fssai: 0, maternity: 1, audio: 0, spiro: 0, vacc: 1, mines: 0, bocw: 0 },
  food:          { factories_pme: 1, pem: 1, drivers: 1, fssai: 1, maternity: 1, audio: 1, spiro: 0, vacc: 1, mines: 0, bocw: 0 },
  mining:        { factories_pme: 1, pem: 1, drivers: 1, fssai: 0, maternity: 1, audio: 1, spiro: 1, vacc: 1, mines: 1, bocw: 0 },
  construction:  { factories_pme: 0, pem: 1, drivers: 1, fssai: 0, maternity: 1, audio: 0, spiro: 0, vacc: 1, mines: 0, bocw: 1 },
  office:        { factories_pme: 0, pem: 1, drivers: 0, fssai: 0, maternity: 1, audio: 0, spiro: 0, vacc: 1, mines: 0, bocw: 0 },
};
const PRESET_LABELS = {
  manufacturing: 'Manufacturing', logistics: 'Logistics / Warehousing',
  food: 'Food processing',        mining: 'Mining / Quarry',
  construction: 'Construction',   office: 'IT / Office',
};

// ─── Helpers ────────────────────────────────────────────────────
const fmt = (n) => Number(n).toLocaleString('en-IN');
const HPS_W = [0.5, 0.3, 0.2];
const hpsScore = (pillars) => {
  let s = 0;
  pillars.forEach((p, i) => {
    s += HPS_W[i] * (Array.isArray(p) ? p[2] : p);
  });
  return Math.round(300 + (s / 100) * 600);
};
const hpsBand = (sc) => {
  if (sc >= 800) return ['Excellent',     '#1f7a4d', '#e7f3ec'];
  if (sc >= 700) return ['Very Good',     '#3D7A52', '#e7f3ec'];
  if (sc >= 600) return ['Good',          '#a8843a', '#fbf2da'];
  if (sc >= 500) return ['Fair',          '#9a6b00', '#fbf2da'];
  return                ['Poor / High risk','#a23b2e', '#f7e6e3'];
};

const statRowId = (label) => {
  if (label.includes('Periodic Medical')) return 'factories_pme';
  if (label.includes('Pre-Employment'))   return 'pem';
  if (label.includes('Drivers'))          return 'drivers';
  if (label.includes('Food-Handler') || label.includes('FSSAI')) return 'fssai';
  if (label.includes('Audiometric'))      return 'audio';
  if (label.includes('Spirometric'))      return 'spiro';
  if (label.includes('Vaccination'))      return 'vacc';
  if (label.includes('Maternity'))        return 'maternity';
  return null;
};
const mapRowId = (statute) => {
  if (statute.includes('Factories Act'))  return 'factories_pme';
  if (statute.includes('ESI Act'))        return 'pem';
  if (statute.includes('Motor Vehicles')) return 'drivers';
  if (statute.includes('FSS'))            return 'fssai';
  if (statute.includes('Maternity'))      return 'maternity';
  if (statute.includes('Bio-Medical'))    return 'bmw';
  if (statute.includes('Mines'))          return 'mines';
  if (statute.includes('BOCW'))           return 'bocw';
  if (statute.includes('DPDP'))           return 'dpdp';
  return null;
};
const activeStat = (d, mOn) => d.statutory.filter((s) => {
  const id = statRowId(s[0]);
  return !id || mOn(id);
});
const fmtDate = (s) => new Date(s).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });

// ═════════════════════════════════════════════════════════════════
// Shared visual components
// ═════════════════════════════════════════════════════════════════

const Card = ({ children, tight, sx = {}, ...rest }) => (
  <Box
    sx={{
      bgcolor: C.paper,
      border: `1px solid ${C.line}`,
      borderRadius: '14px',
      p: tight ? '16px 18px' : '20px 22px',
      ...sx,
    }}
    {...rest}
  >
    {children}
  </Box>
);

const CardHead = ({ title, note }) => (
  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.75 }}>
    <Typography sx={{ fontSize: 16, color: C.forest, fontWeight: 600 }}>{title}</Typography>
    {note && <Typography sx={{ fontSize: 11, color: C.slateL }}>{note}</Typography>}
  </Stack>
);

const Kpi = ({ label, value, suffix, delta, bar }) => (
  <Box
    sx={{
      position: 'relative',
      bgcolor: C.paper,
      border: `1px solid ${C.line}`,
      borderRadius: '13px',
      p: '16px 17px',
      overflow: 'hidden',
    }}
  >
    <Box sx={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 4, bgcolor: bar || C.forest }} />
    <Typography sx={{ fontSize: 11, color: C.slate, fontWeight: 600 }}>{label}</Typography>
    <Box sx={{ mt: 0.75, display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
      <Typography sx={{ fontSize: 32, fontWeight: 700, color: C.forest, lineHeight: 0.95, letterSpacing: '-0.02em' }}>
        {value}
      </Typography>
      {suffix && (
        <Typography sx={{ fontSize: 14, color: C.slate, fontWeight: 600 }}>{suffix}</Typography>
      )}
    </Box>
    {delta && (
      <Typography
        sx={{
          fontSize: 11, mt: 0.5, fontWeight: 600,
          color: delta.kind === 'up' ? C.ok : delta.kind === 'down' ? C.due : C.slate,
        }}
      >
        {delta.arrow} {delta.text}
      </Typography>
    )}
  </Box>
);

const StatPill = ({ code, label }) => {
  const map = {
    ok: { bg: C.okBg, fg: C.ok },
    w:  { bg: C.warnBg, fg: C.warn },
    d:  { bg: C.dueBg, fg: C.due },
    na: { bg: C.wash2, fg: C.slateL },
  };
  const m = map[code] || map.ok;
  return (
    <Box
      component="span"
      sx={{
        fontSize: 10, fontWeight: 700, px: 1.2, py: '3px',
        borderRadius: '5px', bgcolor: m.bg, color: m.fg,
        whiteSpace: 'nowrap', display: 'inline-block',
      }}
    >
      {label}
    </Box>
  );
};

const Banner = ({ kind = 'ok', icon, children }) => {
  const palette = {
    ok:   { bg: C.okBg,   fg: C.ok,   bd: '#bfe0cc' },
    warn: { bg: C.warnBg, fg: C.warn, bd: '#ecd9a3' },
    info: { bg: C.wash,   fg: C.forest, bd: C.line },
  };
  const p = palette[kind] || palette.ok;
  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1.5}
      sx={{
        borderRadius: '12px',
        p: '14px 18px',
        fontSize: 13,
        mb: 2.25,
        bgcolor: p.bg,
        color: p.fg,
        border: `1px solid ${p.bd}`,
      }}
    >
      {icon}
      <Box>{children}</Box>
    </Stack>
  );
};

// Data table that matches the prototype's header / striped-row look.
const DataTable = ({ head, rows }) => (
  <Box sx={{ overflowX: 'auto' }}>
    <Box
      component="table"
      sx={{
        width: '100%',
        borderCollapse: 'collapse',
        fontSize: 12.5,
        '& thead th': {
          bgcolor: C.forest, color: '#fff',
          textAlign: 'left', fontWeight: 600,
          fontSize: 10.5, letterSpacing: '0.04em',
          textTransform: 'uppercase', p: '10px 12px',
        },
        '& tbody td': { p: '10px 12px', borderBottom: `1px solid ${C.line}` },
        '& tbody tr:nth-of-type(even)': { bgcolor: C.wash },
        '& th.c, & td.c': { textAlign: 'center' },
        '& th.r, & td.r': { textAlign: 'right' },
      }}
    >
      <thead>
        <tr>
          {head.map((h, i) => (
            <th key={i} className={h.align === 'c' ? 'c' : h.align === 'r' ? 'r' : ''}>
              {typeof h === 'string' ? h : (h.label ?? '')}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, ri) => (
          <tr key={ri}>
            {row.map((cell, ci) => (
              <td
                key={ci}
                className={head[ci]?.align === 'c' ? 'c' : head[ci]?.align === 'r' ? 'r' : ''}
                style={cell && cell.style}
              >
                {cell && cell.node !== undefined ? cell.node : cell}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </Box>
  </Box>
);

// SVG donut — risk stratification (and any 5-segment % series).
const RiskDonut = ({ series }) => {
  const r = 70;
  const c = 2 * Math.PI * r;
  let off = 0;
  const segs = series.map((s, i) => {
    const len = (c * s[2]) / 100;
    const node = (
      <circle
        key={i}
        cx={80}
        cy={80}
        r={r}
        fill="none"
        stroke={RISK_COLORS[s[0]] || '#888'}
        strokeWidth={20}
        strokeDasharray={`${len} ${c - len}`}
        strokeDashoffset={-off}
      />
    );
    off += len;
    return node;
  });
  const total = series.reduce((a, s) => a + s[1], 0);
  return (
    <Stack direction={{ xs: 'column', md: 'row' }} alignItems="center" spacing={3.25}>
      <Box sx={{ position: 'relative', width: 160, height: 160, flexShrink: 0 }}>
        <svg width="160" height="160" style={{ transform: 'rotate(-90deg)' }}>{segs}</svg>
        <Box sx={{
          position: 'absolute', inset: 0,
          display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center',
        }}>
          <Typography sx={{ fontSize: 36, fontWeight: 700, color: C.forest, lineHeight: 0.8 }}>
            {fmt(total)}
          </Typography>
          <Typography sx={{ fontSize: 10, color: C.slate, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
            screened
          </Typography>
        </Box>
      </Box>
      <Stack spacing={1} sx={{ flex: 1, width: '100%' }}>
        {series.map((s, i) => (
          <Stack key={i} direction="row" alignItems="center" spacing={1.25} sx={{ fontSize: 12.5 }}>
            <Box sx={{ width: 11, height: 11, borderRadius: '6px', bgcolor: RISK_COLORS[s[0]] || '#888' }} />
            <Box sx={{ flex: 1, color: C.ink }}>{s[0]}</Box>
            <Box sx={{ fontWeight: 700, color: C.forest }}>{fmt(s[1])}</Box>
            <Box sx={{ color: C.slate, fontSize: 11, width: 38, textAlign: 'right' }}>{s[2]}%</Box>
          </Stack>
        ))}
      </Stack>
    </Stack>
  );
};

const BarList = ({ rows }) => {
  const max = Math.max(...rows.map((r) => r[1]));
  return (
    <Stack spacing={1.4}>
      {rows.map((r, i) => {
        const w = Math.round((r[1] / max) * 100);
        return (
          <Box key={i}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
              <Typography sx={{ fontSize: 12.5, color: C.ink }}>{r[0]}</Typography>
              <Typography sx={{ fontWeight: 700, fontSize: 12.5, color: C.forest }}>{r[1]}%</Typography>
            </Stack>
            <Box sx={{ height: 9, bgcolor: C.wash2, borderRadius: '6px', overflow: 'hidden' }}>
              <Box sx={{
                height: '100%', width: `${w}%`, borderRadius: '6px',
                background: `linear-gradient(90deg, ${C.forestL}, ${C.forestXL})`,
              }} />
            </Box>
          </Box>
        );
      })}
    </Stack>
  );
};

// HPS gauge — semicircle with band-coloured arc + numeric value.
const Gauge = ({ score }) => {
  const r = 88;
  const cx = 105;
  const cy = 110;
  const len = Math.PI * r;
  const frac = Math.max(0, Math.min(1, (score - 300) / 600));
  const band = hpsBand(score);
  return (
    <Box sx={{ position: 'relative', width: 210, height: 130, mx: 'auto' }}>
      <svg width="210" height="122" viewBox="0 0 210 122">
        <path
          d={`M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`}
          fill="none" stroke="#e7ece9" strokeWidth={15} strokeLinecap="round"
        />
        <path
          d={`M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`}
          fill="none" stroke={band[1]} strokeWidth={15} strokeLinecap="round"
          strokeDasharray={`${frac * len} ${len * 2}`}
        />
      </svg>
      <Box sx={{ position: 'absolute', left: 0, right: 0, bottom: 6, textAlign: 'center' }}>
        <Typography sx={{ fontSize: 44, fontWeight: 700, color: C.forest, lineHeight: 0.8 }}>
          {score}
        </Typography>
        <Typography sx={{ fontSize: 11, color: C.slate, letterSpacing: '0.04em' }}>
          of 900 · Workforce HPS
        </Typography>
        <Box sx={{
          mt: 0.75, display: 'inline-block', fontSize: 12, fontWeight: 700,
          px: 1.5, py: '3px', borderRadius: '100px', bgcolor: band[2], color: band[1],
        }}>
          {band[0]}
        </Box>
      </Box>
    </Box>
  );
};

const PillarRow = ({ pillars }) => (
  <Stack spacing={1.75}>
    {pillars.map((p, i) => (
      <Box
        key={i}
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '150px 1fr 84px' },
          gap: 1.5, alignItems: 'center',
        }}
      >
        <Box>
          <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: C.ink }}>{p[0]}</Typography>
          <Typography sx={{ fontSize: 10.5, color: C.slateL }}>{p[1]}</Typography>
        </Box>
        <Box sx={{ height: 11, bgcolor: C.wash2, borderRadius: '7px', overflow: 'hidden' }}>
          <Box sx={{
            height: '100%', width: `${p[2]}%`, borderRadius: '7px',
            background: `linear-gradient(90deg, ${C.forestL}, ${C.forestXL})`,
          }} />
        </Box>
        <Typography sx={{ textAlign: 'right', fontWeight: 700, color: C.forest, fontSize: 13 }}>
          {p[2]}/100
        </Typography>
      </Box>
    ))}
  </Stack>
);

const HpsDistBar = ({ dist }) => {
  const cmap = { Excellent: '#1f7a4d', 'Very Good': '#3D7A52', Good: '#C8A24B', Fair: '#9a6b00', Poor: '#a23b2e' };
  return (
    <Box>
      <Box sx={{ display: 'flex', height: 18, borderRadius: '9px', overflow: 'hidden', mb: 1.75 }}>
        {dist.map((d, i) => (
          <Box key={i} sx={{ width: `${d[2]}%`, bgcolor: cmap[d[0]] }} title={`${d[0]} ${d[2]}%`} />
        ))}
      </Box>
      <Stack spacing={1}>
        {dist.map((d, i) => (
          <Stack key={i} direction="row" alignItems="center" spacing={1.25} sx={{ fontSize: 12.5 }}>
            <Box sx={{ width: 11, height: 11, borderRadius: '6px', bgcolor: cmap[d[0]] }} />
            <Box sx={{ flex: 1, color: C.ink }}>
              {d[0]} <Box component="small" sx={{ color: C.slateL }}>{d[1]}</Box>
            </Box>
            <Box sx={{ fontWeight: 700, color: C.forest }}>{d[2]}%</Box>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
};

const AlertCard = ({ x, onAction }) => {
  const palette = {
    crit: { bd: C.due,   bg: C.due,   txt: C.due },
    warn: { bd: C.goldD, bg: C.goldD, txt: C.goldD },
    info: { bd: C.blue,  bg: C.blue,  txt: C.blue },
  };
  const p = palette[x.sev] || palette.info;
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '46px 1fr', md: '46px 1fr auto' },
        gap: 1.75, alignItems: 'center',
        border: `1px solid ${C.line}`,
        borderLeft: `5px solid ${p.bd}`,
        borderRadius: '12px', p: '14px 16px',
        bgcolor: C.paper, mb: 1.25,
      }}
    >
      <Box sx={{
        width: 46, height: 46, borderRadius: '11px',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        bgcolor: p.bg, color: '#fff', fontWeight: 700, fontSize: 22, letterSpacing: '-0.02em',
      }}>
        {x.n}
      </Box>
      <Box>
        <Typography sx={{ fontWeight: 600, fontSize: 13.5, color: C.ink }}>{x.t}</Typography>
        <Typography sx={{ fontSize: 11.5, color: C.slate, mt: 0.25 }}>
          {x.ref} · Owner: {x.owner} · Due {fmtDate(x.due)}
        </Typography>
        <Button
          onClick={() => onAction(`${x.act} — actioned`)}
          sx={{
            mt: 0.75, fontSize: 11, fontWeight: 700, color: C.forest,
            bgcolor: C.wash2, border: `1px solid ${C.lineD}`, borderRadius: '7px',
            px: 1.4, py: 0.75, textTransform: 'none', minWidth: 0,
            '&:hover': { bgcolor: C.gold, borderColor: C.gold, color: C.forestD },
          }}
        >
          {x.t.includes('Camp') ? 'Auto-schedule camp' : 'Notify owner & schedule'}
        </Button>
      </Box>
      <Box sx={{ textAlign: 'right', display: { xs: 'none', md: 'block' } }}>
        <Typography sx={{ fontSize: 26, fontWeight: 700, color: p.txt, lineHeight: 0.9, letterSpacing: '-0.02em' }}>
          {x.days}
        </Typography>
        <Typography sx={{ fontSize: 10, color: C.slate, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          days left
        </Typography>
      </Box>
    </Box>
  );
};

// Forest-gradient download tile reused by Reports + Inspection.
const DownloadCard = ({ tag, tagColor = C.forest, title, sub, onClick, stacked, blurb }) => (
  <Button
    onClick={onClick}
    fullWidth
    sx={{
      display: 'flex', alignItems: stacked ? 'flex-start' : 'center',
      flexDirection: stacked ? 'column' : 'row',
      gap: stacked ? 1.75 : 2, p: '18px 20px',
      bgcolor: C.paper, border: `1.5px solid ${C.lineD}`, borderRadius: '14px',
      textTransform: 'none', textAlign: 'left', color: 'inherit',
      transition: '0.16s', justifyContent: 'flex-start',
      '&:hover': {
        borderColor: C.forest,
        boxShadow: '0 6px 22px rgba(15,36,25,0.08)',
        transform: 'translateY(-1px)',
      },
    }}
  >
    <Stack direction="row" spacing={1.75} alignItems="center" sx={{ width: '100%' }}>
      <Box sx={{
        width: 48, height: 48, borderRadius: '11px', flexShrink: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        bgcolor: tagColor, color: '#fff', fontWeight: 800, fontSize: 13,
      }}>
        {tag}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: 15, color: C.forest, fontWeight: 700 }}>{title}</Typography>
        <Typography sx={{ fontSize: 12, color: C.slate }}>{sub}</Typography>
      </Box>
      {!stacked && (
        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ fontSize: 12, fontWeight: 700, color: C.forest }}>
          <span>Download</span><ArrowOutwardIcon sx={{ fontSize: 14 }} />
        </Stack>
      )}
    </Stack>
    {stacked && blurb && (
      <Typography sx={{ fontSize: 11.5, color: C.slate, lineHeight: 1.55 }}>{blurb}</Typography>
    )}
    {stacked && (
      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ fontSize: 12, fontWeight: 700, color: C.forest }}>
        <span>Download</span><ArrowOutwardIcon sx={{ fontSize: 14 }} />
      </Stack>
    )}
  </Button>
);

// Inspection-Desk hero panel and the "opportunity" CTA on HPS.
const Hero = ({ chip, title, lead, meta }) => (
  <Box
    sx={{
      background: `linear-gradient(150deg, ${C.forest}, ${C.forestD})`,
      color: '#fff', borderRadius: '16px', p: '28px 30px',
      position: 'relative', overflow: 'hidden', mb: 2.5,
      '&::after': {
        content: '""', position: 'absolute', top: -80, right: -30,
        width: 280, height: 280,
        background: 'radial-gradient(circle, rgba(200,162,75,0.2) 0%, transparent 68%)',
      },
    }}
  >
    <Box
      component="span"
      sx={{
        position: 'relative', zIndex: 1,
        display: 'inline-flex', alignItems: 'center', gap: 1,
        bgcolor: 'rgba(255,255,255,0.10)',
        border: `1px solid ${C.gold}80`,
        borderRadius: '100px',
        px: 1.75, py: '5px',
        fontSize: 11, fontWeight: 700, letterSpacing: '0.08em',
        textTransform: 'uppercase', color: C.goldL,
      }}
    >
      {chip}
    </Box>
    <Typography sx={{ position: 'relative', zIndex: 1, fontSize: { xs: 22, md: 26 }, fontWeight: 700, mt: 1.75 }}>
      {title}
    </Typography>
    <Typography sx={{ position: 'relative', zIndex: 1, fontSize: 13.5, color: 'rgba(255,255,255,0.72)', maxWidth: 560, mt: 1 }}>
      {lead}
    </Typography>
    {meta && (
      <Stack
        direction="row" flexWrap="wrap" spacing={0}
        sx={{ position: 'relative', zIndex: 1, mt: 2.5, columnGap: 4, rowGap: 1.5 }}
      >
        {meta.map((m, i) => (
          <Box key={i}>
            <Typography sx={{ fontSize: 26, fontWeight: 700, color: C.goldL, lineHeight: 0.9, letterSpacing: '-0.02em' }}>
              {m[0]}
            </Typography>
            <Typography sx={{ fontSize: 11, color: 'rgba(255,255,255,0.6)' }}>{m[1]}</Typography>
          </Box>
        ))}
      </Stack>
    )}
  </Box>
);

// ═════════════════════════════════════════════════════════════════
// PDF helpers (jsPDF + autoTable)
// ═════════════════════════════════════════════════════════════════

const PDF_FOOTER = 'TrustLab Diagnostics Pvt Ltd  ·  CIN U85100TG2020PTC143059  ·  NABL ISO 15189:2022  ·  www.mytrustlab.com';

const brandPDF = (title, subtitle, client, brand) => {
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const W = doc.internal.pageSize.getWidth();
  // Forest band
  doc.setFillColor(15, 36, 25);
  doc.rect(0, 0, W, 74, 'F');
  // White chip with the actual TrustLab logo PNG (falls back to a
  // text mark if the asset hasn't loaded yet).
  if (brand && brand.dataUrl) {
    const lh = 40;
    const lw = lh * (brand.ar || 2.6);
    doc.setFillColor(255, 255, 255);
    doc.roundedRect(34, (74 - lh - 12) / 2, lw + 18, lh + 12, 6, 6, 'F');
    try { doc.addImage(brand.dataUrl, 'PNG', 43, (74 - lh) / 2, lw, lh); } catch (_e) { /* logo decode failed */ }
  } else {
    doc.setFillColor(255, 255, 255);
    doc.roundedRect(34, 18, 150, 40, 6, 6, 'F');
    doc.setTextColor(15, 36, 25);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(18);
    doc.text('TRUSTLAB', 44, 42);
    doc.setTextColor(200, 162, 75);
    doc.setFontSize(8);
    doc.text('DIAGNOSTICS', 44, 52);
  }
  // Title (right)
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  doc.text(title, W - 40, 32, { align: 'right' });
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  doc.setTextColor(225, 225, 215);
  doc.text(subtitle, W - 40, 47, { align: 'right' });
  // Co-brand white strip with client name
  doc.setFillColor(255, 255, 255);
  doc.rect(0, 74, W, 32, 'F');
  doc.setDrawColor(200, 162, 75);
  doc.setLineWidth(2);
  doc.line(0, 106, W, 106);
  doc.setTextColor(120, 132, 124);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);
  doc.text('PREPARED FOR', 40, 86);
  doc.setTextColor(26, 58, 42);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.text(client.name, 40, 99);
  if (client.logo) {
    try {
      const ch = 22;
      const ar = client.logoAR || 2.6;
      const cw = ch * ar;
      doc.addImage(client.logo, client.logoFmt || 'PNG', W - 40 - cw, 79, cw, ch);
    } catch (_e) { /* logo decode failed; skip */ }
  }
  return doc;
};

const footerPDF = (doc) => {
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();
  const n = doc.internal.getNumberOfPages();
  for (let i = 1; i <= n; i += 1) {
    doc.setPage(i);
    doc.setFillColor(15, 36, 25);
    doc.rect(0, H - 30, W, 30, 'F');
    doc.setTextColor(235, 235, 228);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.text(PDF_FOOTER, 40, H - 12);
    doc.setTextColor(200, 162, 75);
    doc.text(`Page ${i} of ${n}`, W - 40, H - 12, { align: 'right' });
  }
};

const secTitle = (doc, txt, y) => {
  if (y > doc.internal.pageSize.getHeight() - 110) {
    doc.addPage();
    y = 56;
  }
  doc.setTextColor(26, 58, 42);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11.5);
  doc.text(txt, 40, y);
  doc.setDrawColor(200, 162, 75);
  doc.setLineWidth(1.6);
  doc.line(40, y + 4, 40 + doc.getTextWidth(txt) + 8, y + 4);
  return y + 18;
};

const paraPDF = (doc, txt, y) => {
  doc.setTextColor(60, 72, 65);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  const lines = doc.splitTextToSize(txt, doc.internal.pageSize.getWidth() - 80);
  if (y + lines.length * 12 > doc.internal.pageSize.getHeight() - 60) {
    doc.addPage();
    y = 56;
  }
  doc.text(lines, 40, y);
  return y + lines.length * 12 + 6;
};

const tablePDF = (doc, head, body, y, opts = {}) => {
  autoTable(doc, {
    startY: y,
    head: [head],
    body,
    theme: 'grid',
    headStyles: { fillColor: [26, 58, 42], textColor: 255, fontSize: 8, fontStyle: 'bold', halign: 'left' },
    bodyStyles: { fontSize: 8, textColor: [40, 50, 45] },
    alternateRowStyles: { fillColor: [245, 248, 246] },
    styles: { cellPadding: 5, lineColor: [223, 228, 224], lineWidth: 0.5, overflow: 'linebreak' },
    columnStyles: opts.col || {},
    margin: { left: 40, right: 40 },
  });
  return doc.lastAutoTable.finalY + 18;
};

// ═════════════════════════════════════════════════════════════════
// View components
// ═════════════════════════════════════════════════════════════════

const FootNote = ({ children }) => (
  <Typography sx={{ mt: 3.75, fontSize: 11, color: C.slateL, borderTop: `1px solid ${C.line}`, pt: 1.75, lineHeight: 1.6 }}>
    {children}
  </Typography>
);

const Overview = ({ ctx }) => {
  const { fy, role, mOn } = ctx;
  const d = DATA[fy];
  const prev = fy === 'FY 2025-26' ? DATA['FY 2024-25'] : null;
  // Banner pulls the latest real inspection record so the line stays
  // accurate even before the rest of the Overview cards are wired to
  // live data.
  const { data: inspections = [] } = useListInspectionsQuery(1);
  const lastInspection = inspections[0];
  // IT-Dose-derived workforce totals — distinct-UHID counts, programme
  // bucketing, YoY block, round attendance. Single endpoint, cached
  // per FY. Falls back to the static dataset's screened number while
  // the cache is loading.
  const { data: agg } = useGetWorkforceAggregateQuery(fy);
  const screenedLive  = agg?.current?.screened ?? d.screened;
  const screenedPrev  = agg?.previous?.screened ?? prev?.screened;
  // Statutory + CAPA also drive KPIs — pull both server-side
  // collections so "compliance score" and "open items" are live too.
  const { data: regRowsRaw = [] } = useListStatutoryQuery(fy);
  const { data: capaRows   = [] } = useListCapaQuery(fy);
  const activeReg = useMemo(
    () => regRowsRaw.filter((r) => !r.mandate_id || mOn(r.mandate_id)),
    [regRowsRaw, mOn],
  );
  const complianceScore = useMemo(() => {
    if (activeReg.length === 0) return 100;
    const pct = activeReg.reduce((a, r) => {
      const m = Math.max(1, r.mandated || 0);
      const dn = Math.min(r.done || 0, r.mandated || 0);
      return a + (dn / m) * 100;
    }, 0);
    return Math.round(pct / activeReg.length);
  }, [activeReg]);
  const openItemsLive = capaRows.filter((c) => c.status !== 'ok').reduce((a, c) => a + (c.count || 0), 0);
  // Eligible — until we have an Employees notebook, we treat the
  // hardcoded number as the upper bound for participation. A real
  // roster CSV import lights this up properly later.
  const eligible      = d.eligible;
  const participation = eligible > 0 ? Math.round((screenedLive / eligible) * 100) : 0;
  const participationPrev = prev && prev.eligible > 0
    ? Math.round((screenedPrev / prev.eligible) * 100)
    : null;

  // Checkup-rounds card pulls the live timeline. Falls back to the
  // hardcoded demo set while a brand-new client has nothing logged
  // yet — keeps the page from looking empty on first sight.
  const { data: liveRounds = [] } = useListRoundsQuery(fy);
  const roundsForCards = liveRounds.length > 0
    ? liveRounds
    : d.rounds.map((r) => ({
        _id: r[0], code: r[0], name: r[1], dateRange: r[2], scope: r[3],
        screened: r[4], participation: r[5],
      }));
  const delta = (cur, pv, goodUp) => {
    if (pv == null) return { kind: 'flat', arrow: '◆', text: 'baseline' };
    const diff = cur - pv;
    const up = diff > 0;
    const good = goodUp ? up : !up;
    const arrow = diff === 0 ? '◆' : up ? '▲' : '▼';
    return { kind: diff === 0 ? 'flat' : good ? 'up' : 'down', arrow, text: `${Math.abs(diff)} vs PY` };
  };
  const kpis = [
    { label: 'Workforce screened', value: fmt(screenedLive), suffix: `/ ${fmt(eligible)} eligible`,
      delta: screenedPrev != null ? delta(screenedLive, screenedPrev, true) : undefined, bar: C.forest },
    { label: 'Participation', value: `${participation}%`,
      delta: participationPrev != null ? delta(participation, participationPrev, true) : undefined, bar: C.forestXL },
    { label: 'Compliance score', value: `${complianceScore}`, suffix: '/100',
      delta: prev && delta(complianceScore, prev.complianceScore, true), bar: C.goldD },
    { label: 'High-risk employees', value: fmt(d.highRisk), suffix: 'flagged · clinical feed pending',
      delta: prev && delta(d.highRisk, prev.highRisk, false), bar: C.due },
    { label: 'Open statutory items', value: openItemsLive, suffix: 'tracked',
      delta: { kind: 'flat', arrow: '◆', text: openItemsLive === 0 ? 'all clear' : 'none overdue' }, bar: C.warn },
  ];
  // Snapshot table uses live register rows when present, else the
  // static demo data so a fresh client still sees the shape.
  const snapshotRows = activeReg.length > 0
    ? activeReg.map((r) => ({
        title:      r.label.split(' — ')[0],
        reference:  r.reference,
        mandated:   r.mandated,
        done:       r.done,
        status:     r.status,
        statusNote: r.statusNote || (r.status === 'ok' ? 'Compliant' : r.status === 'w' ? 'In-window' : 'Action'),
      }))
    : activeStat(d, mOn).map((s) => ({
        title: s[0].split(' — ')[0], reference: s[1],
        mandated: s[2], done: s[3], status: s[4], statusNote: s[5],
      }));

  return (
    <Box>
      <Box
        sx={{
          display: 'grid', gap: 1.75, mb: 0.75,
          gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(5, 1fr)' },
        }}
      >
        {kpis.map((k, i) => <Kpi key={i} {...k} />)}
      </Box>

      {d.openItems > 0 ? (
        <Banner kind="warn" icon={<WarningAmberIcon fontSize="small" />}>
          <Typography sx={{ fontSize: 13 }}>
            <b>Substantially compliant.</b> {d.openItems} items open, each within its statutory window.{' '}
            {lastInspection
              ? <>Last inspection on {fmtDate(lastInspection.inspectedOn)}: <b>{lastInspection.outcome}</b>.</>
              : <>No inspection logged yet — record one from the Inspection Desk.</>}
          </Typography>
        </Banner>
      ) : (
        <Banner kind="ok" icon={<CheckCircleOutlineIcon fontSize="small" />}>
          <Typography sx={{ fontSize: 13 }}>
            <b>Fully compliant.</b> All statutory medical obligations discharged for {fy}.
            {lastInspection && <> Last inspection on {fmtDate(lastInspection.inspectedOn)}: <b>{lastInspection.outcome}</b>.</>}
          </Typography>
        </Banner>
      )}

      <Grid container spacing={2.25} sx={{ mt: 0 }}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardHead title="Workforce Health Distribution" note={fy} />
            <RiskDonut series={d.risk} />
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card>
            <CardHead title="Statutory Compliance Snapshot" note={`${snapshotRows.length} mandates`} />
            <DataTable
              head={[
                'Mandate',
                { label: 'Done', align: 'c' },
                { label: 'Status', align: 'c' },
              ]}
              rows={snapshotRows.map((s) => [
                {
                  node: (
                    <>
                      <Box sx={{ fontWeight: 600 }}>{s.title}</Box>
                      <Box sx={{ fontSize: 10.5, color: C.slateL, mt: '1px' }}>{s.reference}</Box>
                    </>
                  ),
                },
                `${s.done}/${s.mandated}`,
                { node: <StatPill code={s.status} label={s.statusNote} /> },
              ])}
            />
          </Card>
        </Grid>
      </Grid>

      <Card sx={{ mt: 2.25 }}>
        <CardHead
          title="Checkup Rounds this Year"
          note={`${roundsForCards.length} ${roundsForCards.length === 1 ? 'round' : 'rounds'}${liveRounds.length === 0 ? ' (sample)' : ''}`}
        />
        <Stack direction="row" flexWrap="wrap" gap={2}>
          {roundsForCards.map((r) => (
            <Box
              key={r._id}
              sx={{
                flex: '1 1 180px', minWidth: 180,
                bgcolor: C.wash, border: `1px solid ${C.line}`,
                borderRadius: '11px', p: '14px 16px',
              }}
            >
              <Typography sx={{ fontSize: 22, fontWeight: 700, color: C.goldD, letterSpacing: '-0.02em' }}>
                {r.code}
              </Typography>
              <Typography sx={{ fontWeight: 600, fontSize: 13, color: C.forest, mt: '2px', mb: '4px' }}>
                {r.name}
              </Typography>
              <Typography sx={{ fontSize: 11, color: C.slate }}>
                {r.dateRange || '—'}{r.scope ? ` · ${r.scope}` : ''}
              </Typography>
              <Typography sx={{ mt: 1, fontSize: 12, color: C.ink }}>
                <b style={{ color: C.forest }}>{fmt(r.screened)}</b> screened · {r.participation}%
              </Typography>
            </Box>
          ))}
        </Stack>
      </Card>

      <FootNote>
        Figures are the cumulative annual position across all checkup rounds for {fy}. Use the{' '}
        <b>Round</b> selector to scope analytics to a single camp. Data is processed under the
        DPDP Act, 2023 and shown to authorised {role} access only.
      </FootNote>
    </Box>
  );
};

const Analytics = ({ ctx }) => {
  const { fy, round } = ctx;
  const d = DATA[fy];
  // IT-Dose-derived aggregates. `agg.current` carries the FY's
  // distinct UHIDs + programme + department breakdowns; `agg.previous`
  // carries the same shape for the prior FY (powers YoY).
  const { data: agg, isLoading: aggLoading } = useGetWorkforceAggregateQuery(fy);
  const cur  = agg?.current;
  const prev = agg?.previous;

  const scopeName = round === 'ALL'
    ? 'All rounds (annual)'
    : (agg?.rounds?.find((r) => r.code === round)?.name || round);
  const scopeScreened = round === 'ALL' ? (cur?.screened ?? d.screened) : (() => {
    const r = agg?.rounds?.find((rr) => rr.code === round);
    return r?.livescreened ?? r?.screened ?? 0;
  })();
  const annualNote = round === 'ALL' ? '' : ' — risk & biomarker breakdowns shown at annual-cohort level';

  // Departments / programmes — live when available, falling back to
  // the static DATA when the cache hasn't returned anything yet.
  const programmes = cur?.programmes?.length
    ? cur.programmes.map((p) => ({ name: p.label, eligible: p.value, completed: p.value, share: p.share }))
    : d.programmes.map((p) => ({ name: p[0], eligible: p[1], completed: p[2], share: p[3] }));
  const departments = cur?.departments?.length
    ? cur.departments.map((x) => ({ name: x.label, screened: x.value, share: x.share }))
    : d.departments.map((x) => ({ name: x[0], screened: x[1], share: x[2] }));

  return (
    <Box>
      <Banner kind="info" icon={<ScienceOutlinedIcon fontSize="small" sx={{ color: C.forest }} />}>
        <Typography sx={{ fontSize: 13 }}>
          Analytics scope: <b>{scopeName}</b> · {fy} · {fmt(scopeScreened)} screened
          <Box component="span" sx={{ color: C.slate }}>{annualNote}</Box>
          {aggLoading && (
            <Box component="span" sx={{ color: C.slateL, ml: 1 }}>(updating from IT Dose…)</Box>
          )}
        </Typography>
      </Banner>

      <Grid container spacing={2.25}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardHead title="Health Risk Stratification" note="clinical feed pending" />
            <RiskDonut series={d.risk} />
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card>
            <CardHead title="Top Biomarker Concerns" note="% of screened with abnormal result · clinical feed pending" />
            <BarList rows={d.biomarkers} />
          </Card>
        </Grid>
      </Grid>

      <Card sx={{ mt: 2.25 }}>
        <CardHead
          title="Department Distribution"
          note={cur?.departments?.length ? `${cur.screened.toLocaleString('en-IN')} screened` : 'sample data'}
        />
        <DataTable
          head={[
            'Department',
            { label: 'Screened', align: 'c' },
            { label: 'Share', align: 'c' },
            'Coverage',
          ]}
          rows={departments.map((x) => {
            const w = Math.max(2, Math.min(100, Math.round(x.share)));
            return [
              { node: <span style={{ fontWeight: 600 }}>{x.name}</span> },
              fmt(x.screened),
              { node: <b style={{ color: C.forest }}>{x.share}%</b> },
              {
                node: (
                  <Box sx={{ width: '100%', height: 9, bgcolor: C.wash2, borderRadius: '6px', overflow: 'hidden' }}>
                    <Box sx={{
                      height: '100%', width: `${w}%`, borderRadius: '6px',
                      background: `linear-gradient(90deg, ${C.forestL}, ${C.forestXL})`,
                    }} />
                  </Box>
                ),
                style: { width: '34%' },
              },
            ];
          })}
        />
      </Card>

      <Card sx={{ mt: 2.25 }}>
        <CardHead
          title="Programme Participation"
          note={cur?.programmes?.length ? `top ${Math.min(programmes.length, 12)} by uptake` : 'sample data'}
        />
        <DataTable
          head={[
            'Programme',
            { label: 'Screened', align: 'c' },
            { label: 'Share', align: 'c' },
          ]}
          rows={programmes.slice(0, 12).map((p) => [
            { node: <span style={{ fontWeight: 600 }}>{p.name}</span> },
            fmt(p.eligible),
            { node: <b style={{ color: C.forest }}>{p.share}%</b> },
          ])}
        />
      </Card>

      {prev && cur && cur.screened > 0 && (() => {
        const sharePct = (cur.screened) > 0 ? Math.round((cur.screened / Math.max(1, d.eligible)) * 100) : 0;
        const prevSharePct = (prev.screened) > 0 && DATA['FY 2024-25']
          ? Math.round((prev.screened / Math.max(1, DATA['FY 2024-25'].eligible)) * 100)
          : 0;
        const rows = [
          ['Workforce screened', prev.screened, cur.screened, '', true],
          ['Participation',      prevSharePct,  sharePct,     '%', true],
          ['Visits',             prev.visits,   cur.visits,   '', true],
          ['Programmes covered', prev.programmes.length, cur.programmes.length, '', true],
        ];
        return (
          <Card sx={{ mt: 2.25 }}>
            <CardHead title="Year-on-Year Movement" note={`${prev.fy} → ${fy}`} />
            <DataTable
              head={[
                'Metric',
                { label: prev.fy, align: 'c' },
                { label: fy,      align: 'c' },
                { label: 'Movement', align: 'c' },
              ]}
              rows={rows.map((r) => {
                const diff = r[2] - r[1];
                const good = r[4] ? diff > 0 : diff < 0;
                const arr = diff > 0 ? '▲' : diff < 0 ? '▼' : '◆';
                const color = diff === 0 ? C.slate : good ? C.ok : C.due;
                return [
                  { node: <span style={{ fontWeight: 600 }}>{r[0]}</span> },
                  `${r[1]}${r[3]}`,
                  `${r[2]}${r[3]}`,
                  { node: <b style={{ color }}>{arr} {Math.abs(diff)}{r[3]}</b> },
                ];
              })}
            />
          </Card>
        );
      })()}

      <FootNote>
        Screened / participation / programme + department counts come live from the IT Dose feed,
        scoped to your corporate Customer ID and the selected FY. Biomarker prevalence + risk-band
        breakdowns remain sample data until the clinical results feed is connected.
      </FootNote>
    </Box>
  );
};

const HPSView = ({ ctx }) => {
  const { fy } = ctx;
  const d = DATA[fy];
  const e = EXT[fy];
  const sc = hpsScore(e.hps.pillars);
  const prev = hpsScore(e.hps.prevPillars);
  const dlt = sc - prev;

  return (
    <Box>
      <Card>
        <CardHead title="Workforce Health Progressive Score" note={`TrustLife HPS · 300–900 · ${fy}`} />
        <Box sx={{
          display: 'grid', gridTemplateColumns: { xs: '1fr', md: '230px 1fr' },
          gap: 3.25, alignItems: 'center',
        }}>
          <Box>
            <Gauge score={sc} />
            <Typography sx={{
              textAlign: 'center', mt: 1.25, fontSize: 12, fontWeight: 600,
              color: dlt >= 0 ? C.ok : C.due,
            }}>
              {dlt >= 0 ? '▲ +' : '▼ '}{dlt} pts vs prior year
            </Typography>
          </Box>
          <Box>
            <Typography sx={{ fontSize: 12.5, color: C.slate, mb: 1.75 }}>
              A single forward-looking index built from clinical biomarkers, lifestyle and medication
              adherence — trackable year-on-year, unlike a static risk snapshot. Pillar contributions:
            </Typography>
            <PillarRow pillars={e.hps.pillars} />
          </Box>
        </Box>
      </Card>

      <Grid container spacing={2.25} sx={{ mt: 0 }}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardHead title="HPS Band Distribution" note={`${fmt(d.screened)} employees`} />
            <HpsDistBar dist={e.hps.dist} />
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card>
            <CardHead title="HPS by Department" note="lower score = higher risk load" />
            <DataTable
              head={[
                'Department',
                { label: 'HPS', align: 'c' },
                { label: 'Band', align: 'c' },
                'Score band',
              ]}
              rows={[...e.hps.byDept].sort((a, b) => a[1] - b[1]).map((x) => {
                const bb = hpsBand(x[1]);
                const w = Math.round(((x[1] - 300) / 600) * 100);
                return [
                  { node: <span style={{ fontWeight: 600 }}>{x[0]}</span> },
                  { node: <b style={{ color: bb[1] }}>{x[1]}</b> },
                  { node: <StatPill code={x[1] >= 700 ? 'ok' : x[1] >= 600 ? 'w' : 'd'} label={bb[0]} /> },
                  {
                    node: (
                      <Box sx={{ width: '100%', height: 9, bgcolor: C.wash2, borderRadius: '6px', overflow: 'hidden' }}>
                        <Box sx={{ height: '100%', width: `${w}%`, borderRadius: '6px', bgcolor: bb[1] }} />
                      </Box>
                    ),
                    style: { width: '34%' },
                  },
                ];
              })}
            />
          </Card>
        </Grid>
      </Grid>

      <Card sx={{ mt: 2.25 }}>
        <CardHead title="Disease-Progression Forecast" note={`modelled 18 mo → 5 yr · cohort ${fmt(d.screened)}`} />
        <DataTable
          head={[
            'Condition', 'Basis',
            { label: 'At-risk now', align: 'c' },
            { label: 'Horizon', align: 'c' },
            { label: 'No action', align: 'c' },
            { label: 'With intervention', align: 'c' },
            { label: 'Avoidable', align: 'c' },
            { label: 'Est. cost/yr', align: 'c' },
          ]}
          rows={e.forecast.map((f) => [
            { node: <span style={{ fontWeight: 600 }}>{f[0]}</span> },
            { node: <span style={{ fontSize: 11, color: C.slate }}>{f[1]}</span> },
            fmt(f[2]),
            f[3],
            { node: <span style={{ color: C.due, fontWeight: 700 }}>+{f[4]}</span> },
            { node: <span style={{ color: C.ok,  fontWeight: 700 }}>+{f[5]}</span> },
            { node: <b style={{ color: C.forest }}>{f[6]}</b> },
            f[7],
          ])}
        />
      </Card>

      <Box sx={{ mt: 2.25 }}>
        <Hero
          chip="✦ Intervention opportunity"
          title={`${e.forecastAvoid} chronic-disease cases are modelled as avoidable`}
          lead={
            <>
              With structured closed-loop intervention — teleconsults, nutrition, repeat-test
              monitoring and HPS tracking — across the at-risk cohorts above, against an estimated
              avoidable annual healthcare &amp; absenteeism cost of{' '}
              <b style={{ color: C.goldL }}>{e.forecastCost}</b>.
            </>
          }
          meta={[
            [e.forecastAvoid, 'avoidable cases'],
            [e.forecastCost, 'avoidable cost / year'],
            [sc, 'current HPS · target 700+'],
          ]}
        />
      </Box>

      <FootNote>
        HPS follows the TrustLife canonical model (base 300; Biomarkers 50% — status·stability·velocity,
        Lifestyle 30%, Medication 20%; condition-adaptive biomarker weights). Progression figures are
        statistical projections from population prevalence and published conversion rates; per-employee
        accuracy and cost estimates sharpen after 2–3 years of repeat-checkup data per client.
        Modelled estimates — not clinical diagnoses.
      </FootNote>
    </Box>
  );
};

// Inline form to add a new checkup round.
const NewRoundForm = ({ fy, onCreate, busy }) => {
  const [form, setForm] = useState({
    code: '', name: '', dateRange: '', scope: '', panel: '',
    screened: '', participation: '', startsOn: '',
    statusNote: 'Complete',
  });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const reset = () => setForm({
    code: '', name: '', dateRange: '', scope: '', panel: '',
    screened: '', participation: '', startsOn: '',
    statusNote: 'Complete',
  });
  const submit = async () => {
    if (!form.code.trim() || !form.name.trim()) return;
    await onCreate({
      fy,
      code:          form.code.trim(),
      name:          form.name.trim(),
      dateRange:     form.dateRange.trim(),
      scope:         form.scope.trim(),
      panel:         form.panel.trim(),
      screened:      Number(form.screened) || 0,
      participation: Number(form.participation) || 0,
      startsOn:      form.startsOn || null,
      statusNote:    form.statusNote.trim() || 'Complete',
    });
    reset();
  };
  return (
    <Box sx={{ mt: 2, bgcolor: C.wash, border: `1px solid ${C.line}`, borderRadius: '10px', p: 2 }}>
      <Typography sx={{ fontSize: 13, fontWeight: 700, color: C.forest, mb: 1.5 }}>
        Add checkup round
      </Typography>
      <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' } }}>
        <InlineField label="Code" placeholder="R1" value={form.code} onChange={set('code')} />
        <Box sx={{ gridColumn: { md: '2 / 4' } }}>
          <InlineField label="Round name" placeholder="Annual Health Checkup — Round 1" value={form.name} onChange={set('name')} />
        </Box>
        <InlineField label="Start date" type="date" value={form.startsOn} onChange={set('startsOn')} />
        <Box sx={{ gridColumn: { md: '2 / 4' } }}>
          <InlineField label="Date range" placeholder="15 Apr – 30 May 2025" value={form.dateRange} onChange={set('dateRange')} />
        </Box>
        <Box sx={{ gridColumn: { md: '1 / 4' } }}>
          <InlineField label="Scope" placeholder="All sites · 8 camps" value={form.scope} onChange={set('scope')} />
        </Box>
        <Box sx={{ gridColumn: { md: '1 / 4' } }}>
          <InlineField label="Panel" placeholder="AHC full panel · 66 parameters" value={form.panel} onChange={set('panel')} />
        </Box>
        <InlineField label="Screened" type="number" value={form.screened} onChange={set('screened')} />
        <InlineField label="Participation %" type="number" value={form.participation} onChange={set('participation')} />
        <InlineField label="Status note" placeholder="Complete" value={form.statusNote} onChange={set('statusNote')} />
      </Box>
      <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
        <Button
          onClick={submit} disabled={busy || !form.code.trim() || !form.name.trim()}
          sx={{
            fontSize: 12, fontWeight: 700, color: '#fff', bgcolor: C.forest,
            borderRadius: '8px', px: 2.5, py: 1, textTransform: 'none',
            '&:hover': { bgcolor: C.forestD },
            '&.Mui-disabled': { bgcolor: C.lineD, color: '#fff' },
          }}
        >
          {busy ? 'Saving…' : 'Save round'}
        </Button>
      </Stack>
    </Box>
  );
};

const Rounds = ({ ctx }) => {
  const { fy, toast } = ctx;
  const { data: rounds = [], isLoading } = useListRoundsQuery(fy);
  const [createRound, { isLoading: saving  }] = useCreateRoundMutation();
  const [deleteRound, { isLoading: deleting }] = useDeleteRoundMutation();
  const [addOpen, setAddOpen] = useState(false);

  const onCreate = async (body) => {
    try {
      await createRound(body).unwrap();
      toast?.('Round added');
      setAddOpen(false);
    } catch (e) {
      toast?.(e?.data?.message || 'Could not save round');
    }
  };
  const onDelete = async (row) => {
    if (!window.confirm(`Delete round "${row.code} · ${row.name}"?`)) return;
    try {
      await deleteRound({ id: row._id, fy }).unwrap();
      toast?.('Round deleted');
    } catch (e) {
      toast?.(e?.data?.message || 'Could not delete round');
    }
  };

  return (
    <Box>
      <Banner kind="info" icon={<CalendarMonthOutlinedIcon sx={{ color: C.forest }} fontSize="small" />}>
        <Typography sx={{ fontSize: 13 }}>
          <b>{rounds.length} checkup {rounds.length === 1 ? 'round' : 'rounds'}</b> logged for {fy}. A financial year typically carries
          an annual checkup plus one or more statutory / surveillance camps and a new-joiner cohort.
        </Typography>
      </Banner>

      <Card>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.75 }}>
          <Typography sx={{ fontSize: 16, color: C.forest, fontWeight: 600 }}>
            Checkup timeline
          </Typography>
          <AddRowButton open={addOpen} onClick={() => setAddOpen((v) => !v)} addLabel="Add round" />
        </Stack>

        {isLoading && (
          <Typography sx={{ fontSize: 12, color: C.slate, py: 1 }}>Loading rounds…</Typography>
        )}

        {!isLoading && rounds.length === 0 && (
          <Typography sx={{ fontSize: 12.5, color: C.slate, py: 2, textAlign: 'center', bgcolor: C.wash, borderRadius: '8px' }}>
            No checkup rounds logged for {fy}. Tap <b>Add round</b> after the first camp completes.
          </Typography>
        )}

        {!isLoading && rounds.length > 0 && (
          <Box sx={{ position: 'relative', ml: '6px' }}>
            {rounds.map((r, i) => (
              <Box
                key={r._id}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '30px 1fr',
                  gap: 2, pb: 2.75, position: 'relative',
                  '&::before': {
                    content: '""', position: 'absolute',
                    left: 14, top: 24, bottom: i === rounds.length - 1 ? 'auto' : -2,
                    width: 2, bgcolor: C.lineD,
                    display: i === rounds.length - 1 ? 'none' : 'block',
                  },
                }}
              >
                <Box sx={{
                  width: 30, height: 30, borderRadius: '50%',
                  bgcolor: C.paper, border: `2px solid ${C.gold}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 14, fontWeight: 700, color: C.forest, zIndex: 1, letterSpacing: '-0.02em',
                }}>
                  {r.code}
                </Box>
                <Box sx={{
                  bgcolor: C.paper, border: `1px solid ${C.line}`,
                  borderRadius: '13px', p: '16px 18px',
                }}>
                  <Stack direction="row" alignItems="flex-start" justifyContent="space-between" flexWrap="wrap" gap={1}>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography sx={{ fontWeight: 600, fontSize: 15, color: C.forest }}>{r.name}</Typography>
                      <Typography sx={{ fontSize: 11.5, color: C.slate }}>
                        {r.dateRange || '—'}{r.scope ? ` · ${r.scope}` : ''}
                      </Typography>
                      {r.panel && (
                        <Typography sx={{ fontSize: 11, color: C.slateL, mt: 0.5 }}>{r.panel}</Typography>
                      )}
                    </Box>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <StatPill code={r.status} label={r.statusNote || 'Complete'} />
                      <DeleteRowButton onClick={() => onDelete(r)} busy={deleting} />
                    </Stack>
                  </Stack>
                  <Stack direction="row" flexWrap="wrap" sx={{ mt: 1.5, columnGap: 2.75, rowGap: 1 }}>
                    <Box>
                      <Typography sx={{ fontSize: 22, fontWeight: 700, color: C.forest, lineHeight: 0.9, letterSpacing: '-0.02em' }}>
                        {fmt(r.screened)}
                      </Typography>
                      <Typography sx={{ fontSize: 10.5, color: C.slate }}>employees screened</Typography>
                    </Box>
                    <Box>
                      <Typography sx={{ fontSize: 22, fontWeight: 700, color: C.forest, lineHeight: 0.9, letterSpacing: '-0.02em' }}>
                        {r.participation}%
                      </Typography>
                      <Typography sx={{ fontSize: 10.5, color: C.slate }}>round participation</Typography>
                    </Box>
                  </Stack>
                </Box>
              </Box>
            ))}
          </Box>
        )}

        {addOpen && (
          <NewRoundForm fy={fy} onCreate={onCreate} busy={saving} />
        )}
      </Card>

      <FootNote>
        Select a round in the top bar and open <b>Health Analytics</b> to scope the dashboards to a
        single camp. Statutory compliance is always reported on the cumulative annual position.
      </FootNote>
    </Box>
  );
};

// Compact "+" button used by the inline add-row forms.
const AddRowButton = ({ open, onClick, addLabel = 'Add entry' }) => (
  <Button
    onClick={onClick}
    startIcon={<AddCircleOutlineIcon sx={{ fontSize: 16 }} />}
    sx={{
      fontSize: 11.5, fontWeight: 700, color: C.forest,
      bgcolor: open ? C.wash2 : 'transparent',
      border: `1px solid ${C.lineD}`, borderRadius: '7px',
      px: 1.4, py: 0.5, textTransform: 'none', minWidth: 0,
      '&:hover': { bgcolor: C.gold, borderColor: C.gold, color: C.forestD },
    }}
  >
    {open ? 'Close' : addLabel}
  </Button>
);

// Row-level delete affordance shared by the Register + CAPA tables.
const DeleteRowButton = ({ onClick, busy }) => (
  <Button
    onClick={onClick}
    disabled={busy}
    sx={{
      minWidth: 0, p: 0.5, borderRadius: '6px', color: C.due,
      '&:hover': { bgcolor: C.dueBg },
    }}
    title="Delete row"
  >
    <DeleteOutlineIcon sx={{ fontSize: 18 }} />
  </Button>
);

// Tiny field wrapper for the inline add forms.
const InlineField = ({ label, ...rest }) => (
  <Box>
    <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: C.slate, textTransform: 'uppercase', letterSpacing: '0.06em', mb: 0.5 }}>
      {label}
    </Typography>
    <TextField
      {...rest}
      fullWidth size="small"
      sx={{
        '& .MuiOutlinedInput-root': {
          fontSize: 13, bgcolor: '#fff', borderRadius: '8px',
          '& fieldset': { borderColor: C.lineD, borderWidth: '1.5px' },
          '&:hover fieldset': { borderColor: C.lineD },
          '&.Mui-focused fieldset': { borderColor: C.forestXL },
        },
        '& input, & .MuiSelect-select': { padding: '8px 10px' },
      }}
    />
  </Box>
);

// Inline form to add a new register row (one row per mandate per FY).
const NewRegisterForm = ({ fy, onCreate, busy }) => {
  const [form, setForm] = useState({
    mandate_id: 'factories_pme', label: '', reference: '',
    mandated: '', done: '', status: 'ok', statusNote: '', reviewed: '',
  });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const reset = () => setForm({
    mandate_id: 'factories_pme', label: '', reference: '',
    mandated: '', done: '', status: 'ok', statusNote: '', reviewed: '',
  });
  const submit = async () => {
    if (!form.label.trim()) return;
    await onCreate({
      fy,
      mandate_id: form.mandate_id,
      label:      form.label.trim(),
      reference:  form.reference.trim(),
      mandated:   Number(form.mandated) || 0,
      done:       Number(form.done) || 0,
      status:     form.status,
      statusNote: form.statusNote.trim(),
      reviewed:   form.reviewed.trim(),
    });
    reset();
  };

  return (
    <Box sx={{ mt: 2, bgcolor: C.wash, border: `1px solid ${C.line}`, borderRadius: '10px', p: 2 }}>
      <Typography sx={{ fontSize: 13, fontWeight: 700, color: C.forest, mb: 1.5 }}>
        New register entry
      </Typography>
      <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' } }}>
        <InlineField label="Mandate"
          select SelectProps={{ native: true }} value={form.mandate_id} onChange={set('mandate_id')}>
          {MANDATES.map((m) => <option key={m[0]} value={m[0]}>{m[1]}</option>)}
        </InlineField>
        <InlineField label="Status"
          select SelectProps={{ native: true }} value={form.status} onChange={set('status')}>
          <option value="ok">Compliant</option>
          <option value="w">In-window</option>
          <option value="d">Action required</option>
        </InlineField>
        <InlineField label="Status note" placeholder="e.g. 32 in-window" value={form.statusNote} onChange={set('statusNote')} />
        <Box sx={{ gridColumn: { md: '1 / 3' } }}>
          <InlineField label="Display label" placeholder="Factories Act 1948 — Periodic Medical (hazardous)" value={form.label} onChange={set('label')} />
        </Box>
        <InlineField label="Reference" placeholder="§41C(b) · Form 17/33" value={form.reference} onChange={set('reference')} />
        <InlineField label="Mandated" type="number" value={form.mandated} onChange={set('mandated')} />
        <InlineField label="Done" type="number" value={form.done} onChange={set('done')} />
        <InlineField label="Last reviewed" placeholder="12 Apr 2026 or Q1 FY26" value={form.reviewed} onChange={set('reviewed')} />
      </Box>
      <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
        <Button
          onClick={submit} disabled={busy || !form.label.trim()}
          sx={{
            fontSize: 12, fontWeight: 700, color: '#fff', bgcolor: C.forest,
            borderRadius: '8px', px: 2.5, py: 1, textTransform: 'none',
            '&:hover': { bgcolor: C.forestD },
            '&.Mui-disabled': { bgcolor: C.lineD, color: '#fff' },
          }}
        >
          {busy ? 'Saving…' : 'Save entry'}
        </Button>
      </Stack>
    </Box>
  );
};

// Inline form to add a new CAPA item.
const NewCapaForm = ({ fy, onCreate, busy }) => {
  const [form, setForm] = useState({ item: '', count: '', closureWindow: '', owner: '', status: 'w' });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async () => {
    if (!form.item.trim()) return;
    await onCreate({
      fy,
      item:          form.item.trim(),
      count:         Number(form.count) || 0,
      closureWindow: form.closureWindow.trim(),
      owner:         form.owner.trim(),
      status:        form.status,
    });
    setForm({ item: '', count: '', closureWindow: '', owner: '', status: 'w' });
  };
  return (
    <Box sx={{ mt: 2, bgcolor: C.wash, border: `1px solid ${C.line}`, borderRadius: '10px', p: 2 }}>
      <Typography sx={{ fontSize: 13, fontWeight: 700, color: C.forest, mb: 1.5 }}>
        New open item
      </Typography>
      <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' } }}>
        <Box sx={{ gridColumn: { md: '1 / 4' } }}>
          <InlineField label="Open item" placeholder="New-joiner PEMs pending submission to ESIC" value={form.item} onChange={set('item')} />
        </Box>
        <InlineField label="Count" type="number" value={form.count} onChange={set('count')} />
        <InlineField label="Closure window" placeholder="Within 14 days of joining" value={form.closureWindow} onChange={set('closureWindow')} />
        <InlineField label="Owner" placeholder="HR Lead" value={form.owner} onChange={set('owner')} />
        <InlineField label="Status"
          select SelectProps={{ native: true }} value={form.status} onChange={set('status')}>
          <option value="w">In-window</option>
          <option value="d">Action required</option>
          <option value="ok">Closed</option>
        </InlineField>
      </Box>
      <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
        <Button
          onClick={submit} disabled={busy || !form.item.trim()}
          sx={{
            fontSize: 12, fontWeight: 700, color: '#fff', bgcolor: C.forest,
            borderRadius: '8px', px: 2.5, py: 1, textTransform: 'none',
            '&:hover': { bgcolor: C.forestD },
            '&.Mui-disabled': { bgcolor: C.lineD, color: '#fff' },
          }}
        >
          {busy ? 'Saving…' : 'Save item'}
        </Button>
      </Stack>
    </Box>
  );
};

const Statutory = ({ ctx }) => {
  const { fy, mOn, runStatutoryPDF, toast } = ctx;

  // Server-backed register, CAPA and most-recent inspection. Each
  // query is independently cached by RTK so flipping the FY only
  // re-fetches what's keyed by the new year.
  const { data: regRowsRaw = [], isLoading: regLoading } = useListStatutoryQuery(fy);
  const { data: capaRows   = [], isLoading: capaLoading } = useListCapaQuery(fy);
  const { data: inspections = [] } = useListInspectionsQuery(1);

  const [createRegister, { isLoading: regSaving  }] = useCreateStatutoryMutation();
  const [deleteRegister, { isLoading: regDeleting }] = useDeleteStatutoryMutation();
  const [createCapa,     { isLoading: capaSaving  }] = useCreateCapaMutation();
  const [deleteCapa,     { isLoading: capaDeleting }] = useDeleteCapaMutation();

  const [addRegOpen, setAddRegOpen]   = useState(false);
  const [addCapaOpen, setAddCapaOpen] = useState(false);

  // Filter register rows by Settings-driven mandate toggles — a
  // deselected mandate (e.g. mines) is hidden from the page and
  // every derived stat below.
  const activeReg = useMemo(
    () => regRowsRaw.filter((r) => !r.mandate_id || mOn(r.mandate_id)),
    [regRowsRaw, mOn],
  );

  // Compliance score = average of (done / mandated) % across active
  // mandates. Falls back to 100 when nothing has been entered yet so
  // a brand-new client doesn't see a scary 0/100.
  const complianceScore = useMemo(() => {
    if (activeReg.length === 0) return 100;
    const pct = activeReg.reduce((a, r) => {
      const m = Math.max(1, r.mandated || 0);
      const d = Math.min(r.done || 0, r.mandated || 0);
      return a + (d / m) * 100;
    }, 0);
    return Math.round(pct / activeReg.length);
  }, [activeReg]);

  const openCount     = capaRows.filter((c) => c.status !== 'ok').reduce((a, c) => a + (c.count || 0), 0);
  const openMandates  = activeReg.filter((r) => r.status !== 'ok').length;
  const lastInspection = inspections[0];

  const onCreateRegister = async (body) => {
    try {
      await createRegister(body).unwrap();
      toast?.('Register entry added');
      setAddRegOpen(false);
    } catch (e) {
      toast?.(e?.data?.message || 'Could not save entry');
    }
  };
  const onDeleteRegister = async (row) => {
    if (!window.confirm(`Delete register entry "${row.label}"?`)) return;
    try {
      await deleteRegister({ id: row._id, fy }).unwrap();
      toast?.('Entry deleted');
    } catch (e) {
      toast?.(e?.data?.message || 'Could not delete entry');
    }
  };
  const onCreateCapa = async (body) => {
    try {
      await createCapa(body).unwrap();
      toast?.('Open item added');
      setAddCapaOpen(false);
    } catch (e) {
      toast?.(e?.data?.message || 'Could not save item');
    }
  };
  const onDeleteCapa = async (row) => {
    if (!window.confirm(`Delete open item "${row.item}"?`)) return;
    try {
      await deleteCapa({ id: row._id, fy }).unwrap();
      toast?.('Item deleted');
    } catch (e) {
      toast?.(e?.data?.message || 'Could not delete item');
    }
  };

  const inspectionLine = lastInspection
    ? `Last inspection ${fmtDate(lastInspection.inspectedOn)}: ${lastInspection.outcome}.`
    : 'No inspection logged yet — add one from the Inspection Desk.';

  return (
    <Box>
      {openCount === 0 ? (
        <Banner kind="ok" icon={<CheckCircleOutlineIcon fontSize="small" />}>
          <Typography sx={{ fontSize: 13 }}>
            <b>Compliance score {complianceScore}/100.</b>{' '}
            {activeReg.length === 0 ? 'No register entries yet — add the first one below.' : 'All mandates fully discharged.'}{' '}
            {inspectionLine}
          </Typography>
        </Banner>
      ) : (
        <Banner kind="warn" icon={<WarningAmberIcon fontSize="small" />}>
          <Typography sx={{ fontSize: 13 }}>
            <b>Compliance score {complianceScore}/100.</b> {openCount} items open across {openMandates} mandates,
            each within its statutory window. {inspectionLine}
          </Typography>
        </Banner>
      )}

      <Card>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.75 }}>
          <Typography sx={{ fontSize: 16, color: C.forest, fontWeight: 600 }}>
            Statutory Compliance Register
          </Typography>
          <Stack direction="row" spacing={1.25} alignItems="center">
            <Typography sx={{ fontSize: 11, color: C.slateL }}>{fy}</Typography>
            <AddRowButton open={addRegOpen} onClick={() => setAddRegOpen((v) => !v)} addLabel="Add entry" />
          </Stack>
        </Stack>

        {regLoading && (
          <Typography sx={{ fontSize: 12, color: C.slate, py: 1 }}>Loading register…</Typography>
        )}

        {!regLoading && activeReg.length > 0 && (
          <DataTable
            head={[
              'Statutory category', 'Reference',
              { label: 'Mandated', align: 'c' },
              { label: 'Done', align: 'c' },
              { label: 'Status', align: 'c' },
              'Reviewed',
              { label: '', align: 'c' },
            ]}
            rows={activeReg.map((r) => [
              { node: <span style={{ fontWeight: 600 }}>{r.label}</span> },
              { node: <span style={{ fontSize: 11, color: C.slate }}>{r.reference || '—'}</span> },
              r.mandated,
              r.done,
              {
                node: (
                  <StatPill
                    code={r.status}
                    label={r.statusNote || (r.status === 'ok' ? 'Compliant' : r.status === 'w' ? 'In-window' : 'Action')}
                  />
                ),
              },
              r.reviewed || '—',
              { node: <DeleteRowButton onClick={() => onDeleteRegister(r)} busy={regDeleting} /> },
            ])}
          />
        )}

        {!regLoading && activeReg.length === 0 && (
          <Typography sx={{ fontSize: 12.5, color: C.slate, py: 2, textAlign: 'center', bgcolor: C.wash, borderRadius: '8px' }}>
            No register entries yet. Tap <b>Add entry</b> to log your first applicable mandate.
          </Typography>
        )}

        {addRegOpen && (
          <NewRegisterForm fy={fy} onCreate={onCreateRegister} busy={regSaving} />
        )}
      </Card>

      <Card sx={{ mt: 2.25 }}>
        <CardHead title="Statutory Mandate Reference Map" note="all Indian mandates · applicability" />
        <DataTable
          head={[
            'Governing statute', 'Obligation',
            { label: 'Applies', align: 'c' },
            'How discharged',
          ]}
          rows={MANDATE_MAP.map((m) => {
            const id = mapRowId(m[0]);
            const on = id ? mOn(id) : true;
            const apTxt = on
              ? (m[0].includes('Bio-Medical') ? 'Service-side' : 'Applicable')
              : 'Not applicable';
            const ap = on ? (m[0].includes('Bio-Medical') ? 'w' : 'ok') : 'na';
            const how = id && !on ? 'Deselected for this client in Settings — excluded from reports.' : m[4];
            return [
              {
                node: (
                  <>
                    <Box sx={{ fontWeight: 600 }}>{m[0]}</Box>
                    <Box sx={{ fontSize: 10.5, color: C.slateL, mt: '1px' }}>{m[1]}</Box>
                  </>
                ),
              },
              { node: <span style={{ fontSize: 11, color: C.slate }}>{m[2]}</span> },
              { node: <StatPill code={ap} label={apTxt} /> },
              { node: <span style={{ fontSize: 11, color: C.slate }}>{how}</span> },
            ];
          })}
        />
      </Card>

      <Card sx={{ mt: 2.25 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.75 }}>
          <Typography sx={{ fontSize: 16, color: C.forest, fontWeight: 600 }}>
            Open Items &amp; Action Register (CAPA)
          </Typography>
          <Stack direction="row" spacing={1.25} alignItems="center">
            <Typography sx={{ fontSize: 11, color: C.slateL }}>{capaRows.length} items</Typography>
            <AddRowButton open={addCapaOpen} onClick={() => setAddCapaOpen((v) => !v)} addLabel="Add open item" />
          </Stack>
        </Stack>

        {capaLoading && (
          <Typography sx={{ fontSize: 12, color: C.slate, py: 1 }}>Loading CAPA…</Typography>
        )}

        {!capaLoading && capaRows.length > 0 && (
          <DataTable
            head={[
              'Open item',
              { label: 'Count', align: 'c' },
              'Closure window',
              'Owner',
              { label: 'Status', align: 'c' },
              { label: '', align: 'c' },
            ]}
            rows={capaRows.map((x) => [
              x.item,
              x.count,
              x.closureWindow || '—',
              { node: <span style={{ color: C.slate }}>{x.owner || '—'}</span> },
              { node: <StatPill code={x.status} label={x.status === 'ok' ? 'Closed' : x.status === 'd' ? 'Action' : 'In-window'} /> },
              { node: <DeleteRowButton onClick={() => onDeleteCapa(x)} busy={capaDeleting} /> },
            ])}
          />
        )}

        {!capaLoading && capaRows.length === 0 && (
          <Typography sx={{ fontSize: 12.5, color: C.slate, py: 2, textAlign: 'center', bgcolor: C.wash, borderRadius: '8px' }}>
            No open items. Tap <b>Add open item</b> when something needs tracking.
          </Typography>
        )}

        {addCapaOpen && (
          <NewCapaForm fy={fy} onCreate={onCreateCapa} busy={capaSaving} />
        )}
      </Card>

      <Card sx={{ mt: 2.25 }}>
        <CardHead title="Download" />
        <DownloadCard
          tag="PDF" tagColor={C.goldD}
          title="Statutory Compliance Report"
          sub={`Inspector-ready · ${fy} · all mandates, registers & CAPA`}
          onClick={runStatutoryPDF}
        />
      </Card>

      <FootNote>
        Form numbers (17/33) follow the Model Factories Rules; operative numbering is per the State
        Factories Rules applicable at each site. This register indexes the primary statutory
        registers and certificates retained at each site.
      </FootNote>
    </Box>
  );
};

// Inline form to log a new inspection event.
const NewInspectionForm = ({ onCreate, busy }) => {
  const [form, setForm] = useState({
    inspectedOn: new Date().toISOString().slice(0, 10),
    authority: '', reportServed: '', outcome: 'Cleared', notes: '',
  });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async () => {
    if (!form.authority.trim()) return;
    await onCreate(form);
    setForm({
      inspectedOn: new Date().toISOString().slice(0, 10),
      authority: '', reportServed: '', outcome: 'Cleared', notes: '',
    });
  };
  return (
    <Box sx={{ mt: 2, bgcolor: C.wash, border: `1px solid ${C.line}`, borderRadius: '10px', p: 2 }}>
      <Typography sx={{ fontSize: 13, fontWeight: 700, color: C.forest, mb: 1.5 }}>
        Log inspection
      </Typography>
      <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' } }}>
        <InlineField label="Date" type="date" value={form.inspectedOn} onChange={set('inspectedOn')} />
        <InlineField label="Outcome"
          select SelectProps={{ native: true }} value={form.outcome} onChange={set('outcome')}>
          <option value="Cleared">Cleared</option>
          <option value="Action required">Action required</option>
          <option value="Pending review">Pending review</option>
        </InlineField>
        <Box sx={{ gridColumn: { md: '1 / 3' } }}>
          <InlineField label="Inspector / authority" placeholder="Factory Inspectorate, Telangana" value={form.authority} onChange={set('authority')} />
        </Box>
        <Box sx={{ gridColumn: { md: '1 / 3' } }}>
          <InlineField label="Report served" placeholder="Statutory Compliance Report FY 2025-26" value={form.reportServed} onChange={set('reportServed')} />
        </Box>
      </Box>
      <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
        <Button
          onClick={submit} disabled={busy || !form.authority.trim()}
          sx={{
            fontSize: 12, fontWeight: 700, color: '#fff', bgcolor: C.forest,
            borderRadius: '8px', px: 2.5, py: 1, textTransform: 'none',
            '&:hover': { bgcolor: C.forestD },
            '&.Mui-disabled': { bgcolor: C.lineD, color: '#fff' },
          }}
        >
          {busy ? 'Saving…' : 'Save inspection'}
        </Button>
      </Stack>
    </Box>
  );
};

const Inspection = ({ ctx }) => {
  const { fy, mOn, runStatutoryPDF, runHrPDF, client, toast } = ctx;

  // Server-backed register + inspections. Compliance score derived
  // live from active register rows, matching the Statutory page.
  const { data: regRowsRaw = [] }    = useListStatutoryQuery(fy);
  const { data: inspections = [] }   = useListInspectionsQuery(20);
  const [createInspection, { isLoading: inspSaving  }] = useCreateInspectionMutation();
  const [deleteInspection, { isLoading: inspDeleting }] = useDeleteInspectionMutation();

  const [addOpen, setAddOpen] = useState(false);

  const active = useMemo(
    () => regRowsRaw.filter((r) => !r.mandate_id || mOn(r.mandate_id)),
    [regRowsRaw, mOn],
  );
  const complianceScore = useMemo(() => {
    if (active.length === 0) return 100;
    const pct = active.reduce((a, r) => {
      const m = Math.max(1, r.mandated || 0);
      const d = Math.min(r.done || 0, r.mandated || 0);
      return a + (d / m) * 100;
    }, 0);
    return Math.round(pct / active.length);
  }, [active]);

  const onCreate = async (body) => {
    try {
      await createInspection(body).unwrap();
      toast?.('Inspection logged');
      setAddOpen(false);
    } catch (e) {
      toast?.(e?.data?.message || 'Could not log inspection');
    }
  };
  const onDelete = async (row) => {
    if (!window.confirm(`Delete inspection record for ${fmtDate(row.inspectedOn)}?`)) return;
    try {
      await deleteInspection(row._id).unwrap();
      toast?.('Inspection record deleted');
    } catch (e) {
      toast?.(e?.data?.message || 'Could not delete record');
    }
  };

  const last = inspections[0];

  return (
    <Box>
      <Hero
        chip="● Inspection Desk"
        title="Statutory Compliance — ready for inspection"
        lead="One consolidated, NABL-backed report covering every statutory medical obligation for the selected financial year. Hand it to the visiting Factory / ESIC / DGMS inspector, or download a fresh copy below."
        meta={[
          [`${complianceScore}/100`, 'Compliance score'],
          [fy, 'Financial year'],
          [
            last ? fmtDate(last.inspectedOn) : '—',
            last ? `Last inspection · ${last.outcome}` : 'No inspection logged yet',
          ],
          [client.sites, 'Sites covered'],
        ]}
      />

      <Grid container spacing={2.25}>
        <Grid item xs={12} md={6}>
          <DownloadCard
            tag="PDF" tagColor={C.forest}
            title="Statutory Compliance Report"
            sub="Full mandate register · Form 17/33 · ESI · Form 1A · FSSAI · surveillance · CAPA · declaration"
            onClick={runStatutoryPDF}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <DownloadCard
            tag="PDF" tagColor={C.goldD}
            title="HR Workforce Health Report"
            sub="Analytics & programme outcomes — internal use, contains aggregate health data"
            onClick={runHrPDF}
          />
        </Grid>
      </Grid>

      <Card sx={{ mt: 2.25 }}>
        <CardHead title="What the report certifies" />
        {active.length > 0 ? (
          <>
            <DataTable
              head={[
                'Statutory category', 'Reference',
                { label: 'Mandated', align: 'c' },
                { label: 'Done', align: 'c' },
                { label: 'Status', align: 'c' },
              ]}
              rows={active.map((r) => [
                { node: <span style={{ fontWeight: 600 }}>{r.label}</span> },
                { node: <span style={{ fontSize: 11, color: C.slate }}>{r.reference || '—'}</span> },
                r.mandated,
                r.done,
                {
                  node: (
                    <StatPill
                      code={r.status}
                      label={r.statusNote || (r.status === 'ok' ? 'Compliant' : r.status === 'w' ? 'In-window' : 'Action')}
                    />
                  ),
                },
              ])}
            />
            <Stack direction="row" flexWrap="wrap" gap={1.25} sx={{ mt: 1.5 }}>
              <PillChip code="ok" label={`${active.filter((r) => r.status === 'ok').length} fully compliant`} />
              <PillChip code="w"  label={`${active.filter((r) => r.status === 'w').length} in-window`} />
              {active.filter((r) => r.status === 'd').length > 0 && (
                <PillChip code="d" label={`${active.filter((r) => r.status === 'd').length} requiring action`} />
              )}
            </Stack>
          </>
        ) : (
          <Typography sx={{ fontSize: 12.5, color: C.slate, py: 2, textAlign: 'center', bgcolor: C.wash, borderRadius: '8px' }}>
            No register entries yet — populate them from the Statutory Compliance page first.
          </Typography>
        )}
      </Card>

      <Card sx={{ mt: 2.25 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.75 }}>
          <Typography sx={{ fontSize: 16, color: C.forest, fontWeight: 600 }}>
            Inspector access record
          </Typography>
          <Stack direction="row" spacing={1.25} alignItems="center">
            <Typography sx={{ fontSize: 11, color: C.slateL }}>audit trail</Typography>
            <AddRowButton open={addOpen} onClick={() => setAddOpen((v) => !v)} addLabel="Log inspection" />
          </Stack>
        </Stack>

        {inspections.length > 0 ? (
          <DataTable
            head={[
              'Date', 'Inspector / authority', 'Report served',
              { label: 'Outcome', align: 'c' },
              { label: '', align: 'c' },
            ]}
            rows={inspections.map((row) => [
              fmtDate(row.inspectedOn),
              row.authority || '—',
              row.reportServed || '—',
              {
                node: (
                  <StatPill
                    code={row.outcome === 'Cleared' ? 'ok' : row.outcome === 'Action required' ? 'd' : 'w'}
                    label={row.outcome}
                  />
                ),
              },
              { node: <DeleteRowButton onClick={() => onDelete(row)} busy={inspDeleting} /> },
            ])}
          />
        ) : (
          <Typography sx={{ fontSize: 12.5, color: C.slate, py: 2, textAlign: 'center', bgcolor: C.wash, borderRadius: '8px' }}>
            No inspections logged yet — tap <b>Log inspection</b> after the next visit.
          </Typography>
        )}

        {addOpen && (
          <NewInspectionForm onCreate={onCreate} busy={inspSaving} />
        )}
      </Card>

      <FootNote>
        Generated reports carry the TrustLab masthead, document reference TL-OHC-CP-001, NABL
        accreditation footer and a signature block for the Certifying Surgeon and TrustLab
        Authorised Signatory.
      </FootNote>
    </Box>
  );
};

// "Pillbar" chip used in the inspection summary.
const PillChip = ({ code, label }) => {
  const map = {
    ok: { bg: C.okBg,   fg: C.ok },
    w:  { bg: C.warnBg, fg: C.warn },
    d:  { bg: C.dueBg,  fg: C.due },
  };
  const m = map[code] || map.ok;
  return (
    <Box sx={{
      fontSize: 11.5, fontWeight: 600, px: 1.5, py: '5px',
      borderRadius: '100px', bgcolor: m.bg, color: m.fg,
    }}>
      {label}
    </Box>
  );
};

// Inline form to track a new certificate expiry / renewal deadline.
const NewCertificateForm = ({ onCreate, busy }) => {
  const [form, setForm] = useState({
    title: '', reference: '', mandate_id: '', count: '',
    owner: '', action: '',
    dueDate: new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10),
  });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async () => {
    if (!form.title.trim() || !form.dueDate) return;
    await onCreate({
      ...form,
      title:      form.title.trim(),
      reference:  form.reference.trim(),
      mandate_id: form.mandate_id || '',
      count:      Number(form.count) || 0,
      owner:      form.owner.trim(),
      action:     form.action.trim(),
    });
    setForm({
      title: '', reference: '', mandate_id: '', count: '',
      owner: '', action: '',
      dueDate: new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10),
    });
  };
  return (
    <Box sx={{ mt: 2, bgcolor: C.wash, border: `1px solid ${C.line}`, borderRadius: '10px', p: 2 }}>
      <Typography sx={{ fontSize: 13, fontWeight: 700, color: C.forest, mb: 1.5 }}>
        Track new deadline
      </Typography>
      <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' } }}>
        <Box sx={{ gridColumn: { md: '1 / 4' } }}>
          <InlineField label="Title" placeholder="Drivers' Fitness (Form 1A) — renewal" value={form.title} onChange={set('title')} />
        </Box>
        <InlineField label="Reference" placeholder="CMV Rules 1989 · Form 1A" value={form.reference} onChange={set('reference')} />
        <InlineField label="Mandate"
          select SelectProps={{ native: true }} value={form.mandate_id} onChange={set('mandate_id')}>
          <option value="">— None —</option>
          {MANDATES.map((m) => <option key={m[0]} value={m[0]}>{m[1]}</option>)}
        </InlineField>
        <InlineField label="Due date" type="date" value={form.dueDate} onChange={set('dueDate')} />
        <InlineField label="Count" type="number" value={form.count} onChange={set('count')} />
        <InlineField label="Owner" placeholder="HR Lead" value={form.owner} onChange={set('owner')} />
        <Box sx={{ gridColumn: { md: '1 / 4' } }}>
          <InlineField label="Action to take" placeholder="Renew driver medical fitness certificates" value={form.action} onChange={set('action')} />
        </Box>
      </Box>
      <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
        <Button
          onClick={submit} disabled={busy || !form.title.trim() || !form.dueDate}
          sx={{
            fontSize: 12, fontWeight: 700, color: '#fff', bgcolor: C.forest,
            borderRadius: '8px', px: 2.5, py: 1, textTransform: 'none',
            '&:hover': { bgcolor: C.forestD },
            '&.Mui-disabled': { bgcolor: C.lineD, color: '#fff' },
          }}
        >
          {busy ? 'Saving…' : 'Track deadline'}
        </Button>
      </Stack>
    </Box>
  );
};

const Alerts = ({ ctx }) => {
  const { mOn, toast } = ctx;
  const { data: rows = [], isLoading } = useListCertificatesQuery();
  const [createCertificate, { isLoading: saving   }] = useCreateCertificateMutation();
  const [deleteCertificate, { isLoading: deleting }] = useDeleteCertificateMutation();
  const [addOpen, setAddOpen] = useState(false);

  // Apply mandate-toggle filtering + derive severity from today's date.
  const items = useMemo(() => {
    const now = new Date();
    return rows
      .filter((r) => !r.mandate_id || mOn(r.mandate_id))
      .map((r) => {
        const due  = new Date(r.dueDate);
        const days = Math.ceil((due - now) / 86400000);
        const sev  = days <= 15 ? 'crit' : days <= 45 ? 'warn' : 'info';
        return {
          _id:   r._id,
          t:     r.title,
          ref:   r.reference,
          n:     r.count,
          due:   r.dueDate,
          days, owner: r.owner, act: r.action, sev,
        };
      })
      .sort((a, b) => a.days - b.days);
  }, [rows, mOn]);
  const crit = items.filter((x) => x.sev === 'crit').length;
  const warn = items.filter((x) => x.sev === 'warn').length;

  const onCreate = async (body) => {
    try {
      await createCertificate(body).unwrap();
      toast?.('Deadline added');
      setAddOpen(false);
    } catch (e) {
      toast?.(e?.data?.message || 'Could not save deadline');
    }
  };
  const onDelete = async (row) => {
    if (!window.confirm(`Stop tracking "${row.t}"?`)) return;
    try {
      await deleteCertificate(row._id).unwrap();
      toast?.('Deadline removed');
    } catch (e) {
      toast?.(e?.data?.message || 'Could not delete deadline');
    }
  };

  return (
    <Box>
      <Banner
        kind={crit ? 'warn' : 'ok'}
        icon={crit ? <AccessTimeIcon fontSize="small" /> : <CheckCircleOutlineIcon fontSize="small" />}
      >
        <Typography sx={{ fontSize: 13 }}>
          <b>{items.length} upcoming compliance {items.length === 1 ? 'deadline' : 'deadlines'}.</b>{' '}
          {crit
            ? `${crit} critical (≤15 days) · ${warn} due soon (≤45 days).`
            : 'None critical.'}{' '}
          This view is live and forward-looking — independent of the year selected above.
        </Typography>
      </Banner>

      <Card>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.75 }}>
          <Typography sx={{ fontSize: 16, color: C.forest, fontWeight: 600 }}>
            Statutory Deadline Engine
          </Typography>
          <Stack direction="row" spacing={1.25} alignItems="center">
            <Typography sx={{ fontSize: 11, color: C.slateL }}>
              auto-tracked from registers &amp; certificate expiries
            </Typography>
            <AddRowButton open={addOpen} onClick={() => setAddOpen((v) => !v)} addLabel="Track deadline" />
          </Stack>
        </Stack>

        {isLoading && (
          <Typography sx={{ fontSize: 12, color: C.slate, py: 1 }}>Loading deadlines…</Typography>
        )}

        {!isLoading && items.length === 0 && (
          <Typography sx={{ fontSize: 12.5, color: C.slate, py: 2, textAlign: 'center', bgcolor: C.wash, borderRadius: '8px' }}>
            No deadlines tracked yet. Tap <b>Track deadline</b> to log certificate expiries and renewal cycles.
          </Typography>
        )}

        {items.map((x) => (
          <Box key={x._id} sx={{ position: 'relative' }}>
            <AlertCard x={x} onAction={toast} />
            <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
              <DeleteRowButton onClick={() => onDelete(x)} busy={deleting} />
            </Box>
          </Box>
        ))}

        {addOpen && (
          <NewCertificateForm onCreate={onCreate} busy={saving} />
        )}
      </Card>

      <Card sx={{ mt: 2.25 }}>
        <CardHead title="How it works" />
        <Typography sx={{ fontSize: 12.5, color: C.slate, lineHeight: 1.6 }}>
          Every certificate expiry, statutory cycle and submission window in the compliance register
          is tracked against its due date. The engine surfaces what is coming due, ranks by urgency,
          assigns an owner, and lets you trigger the next camp or notify the responsible person in
          one tap — moving you from <i>reporting</i> compliance to <i>guaranteeing</i> it. In
          production this fires email/WhatsApp/HRMS reminders on the 45 / 15 / 5-day marks.
        </Typography>
      </Card>

      <FootNote>
        Due dates are computed live against today’s date. Deselecting a mandate in <b>Settings</b>
        also hides its deadlines here, so the alert feed always matches the client’s active
        statutory scope.
      </FootNote>
    </Box>
  );
};

const ESG = ({ ctx }) => {
  const { fy, runEsgPDF, client, mOn } = ctx;
  const d = DATA[fy];
  const e = EXT[fy];
  // Live aggregates + statutory register so the ESG indicators reflect
  // this client's actual numbers, not the demo dataset.
  const { data: agg } = useGetWorkforceAggregateQuery(fy);
  const { data: regRowsRaw = [] } = useListStatutoryQuery(fy);
  const screenedLive  = agg?.current?.screened ?? d.screened;
  const eligibleLive  = d.eligible; // until Employee roster lands
  const participationLive = eligibleLive > 0 ? Math.round((screenedLive / eligibleLive) * 100) : 0;
  const activeReg = regRowsRaw.filter((r) => !r.mandate_id || mOn(r.mandate_id));
  const complianceScore = activeReg.length === 0
    ? 100
    : Math.round(activeReg.reduce((a, r) => {
        const m  = Math.max(1, r.mandated || 0);
        const dn = Math.min(r.done || 0, r.mandated || 0);
        return a + (dn / m) * 100;
      }, 0) / activeReg.length);

  const gri = [
    ['GRI 403-1', 'OH&S management system', 'Maintained by the occupier; medical surveillance delivered by TrustLab under NABL ISO 15189:2022 accreditation.'],
    ['GRI 403-3', 'Occupational health services', `Pre-employment, periodic & statutory medical examinations across ${client.sites} sites with ${client.ohc} on-site OHC.`],
    ['GRI 403-5', 'Worker training / advisory on health', 'Post-screening individual health advisories issued to all screened workers; counselling for high-risk cases.'],
    ['GRI 403-6', 'Promotion of worker health', `AHC (${participationLive}% participation), Executive Health Programme, vaccination, audiometry/spirometry surveillance, stress & burnout screen; access via 14-branch network + teleconsult.`],
    ['GRI 403-9', 'Work-related injuries', 'Injury register maintained by occupier; pre-employment fitness & return-to-work assessments supported by TrustLab.'],
    ['GRI 403-10', 'Work-related ill health', `Surveillance for noise-induced hearing loss (ISO 1999:2013) and dust-related impairment (ATS/ERS 2019); ${d.criticalRefer} urgent clinical referrals this period.`],
  ];
  const brsr = [
    ['Workers covered by occupational health services', '100% of eligible workforce'],
    ['Annual health screening participation', `${participationLive}% (${fmt(screenedLive)} of ${fmt(eligibleLive)})`],
    ['Statutory medical compliance score', `${complianceScore} / 100`],
    ['Health & well-being measures', 'AHC · Executive Health · Vaccination · Audiometry/Spirometry surveillance · Stress & Burnout screen'],
    ['High-risk employees identified & referred', `${fmt(d.highRisk)} flagged · ${d.criticalRefer} urgent referrals (clinical feed pending)`],
    ['Progressive health intelligence', `Workforce HPS ${hpsScore(e.hps.pillars)}/900 · ${e.forecastAvoid} chronic-disease cases modelled as avoidable (clinical feed pending)`],
    ['Access to non-occupational medical services', 'TrustLab 14-branch pan-India network + teleconsultation'],
  ];

  return (
    <Box>
      <Banner kind="info" icon={<EnergySavingsLeafOutlinedIcon fontSize="small" sx={{ color: C.forest }} />}>
        <Typography sx={{ fontSize: 13 }}>
          Health &amp; well-being indicators mapped to <b>GRI 403 (Occupational Health &amp; Safety)</b> and{' '}
          <b>BRSR Principle 3</b> — disclosure-ready for your sustainability report. India’s top-1000
          listed companies file BRSR; their suppliers increasingly must too.
        </Typography>
      </Banner>

      <Card>
        <CardHead title="GRI 403 — Occupational Health & Safety" note={fy} />
        <DataTable
          head={['Disclosure', 'Indicator', 'Position']}
          rows={gri.map((r) => [
            { node: <span style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{r[0]}</span> },
            { node: <span style={{ fontWeight: 600 }}>{r[1]}</span> },
            { node: <span style={{ fontSize: 11.5, color: C.slate }}>{r[2]}</span> },
          ])}
        />
      </Card>

      <Card sx={{ mt: 2.25 }}>
        <CardHead title="BRSR Principle 3 — Employee Well-being" note="Leadership / Essential indicators" />
        <DataTable
          head={['Indicator', 'Value']}
          rows={brsr.map((r) => [
            { node: <span style={{ fontWeight: 600 }}>{r[0]}</span> },
            r[1],
          ])}
        />
      </Card>

      <Card sx={{ mt: 2.25 }}>
        <CardHead title="Download" />
        <DownloadCard
          tag="ESG" tagColor={C.forestXL}
          title="ESG & BRSR Health Disclosure"
          sub={`${fy} · GRI 403 + BRSR Principle 3 · sustainability-report ready`}
          onClick={runEsgPDF}
        />
      </Card>

      <FootNote>
        Mapping aligns TrustLab-delivered occupational-health data to the GRI 403 (2018) and SEBI
        BRSR Principle-3 indicators. Injury data (403-9) is maintained by the occupier; this
        disclosure covers the medical-surveillance and health-promotion components.
      </FootNote>
    </Box>
  );
};

const Reports = ({ ctx }) => {
  const { fy, runHrPDF, runStatutoryPDF, runEsgPDF } = ctx;
  const matrix = [
    ['Workforce KPI & participation', '✓', '✓', '✓'],
    ['Risk stratification & biomarkers', '✓', '—', '—'],
    ['Progressive HPS & forecasting', '—', '—', 'summary'],
    ['Department & programme analytics', '✓', '—', '—'],
    ['Year-on-year movement', '✓', '—', '—'],
    ['Statutory compliance register', 'summary', '✓ full', 'summary'],
    ['Statutory mandate reference map', '—', '✓', '—'],
    ['Surveillance methodology (ISO/ATS-ERS)', '—', '✓', '✓'],
    ['Open-items CAPA register', '—', '✓', '—'],
    ['GRI 403 indicators', '—', '—', '✓'],
    ['BRSR Principle-3 indicators', '—', '—', '✓'],
    ['Certifying Surgeon declaration', '—', '✓', '—'],
  ];
  return (
    <Box>
      <Banner kind="info" icon={<ArticleOutlinedIcon fontSize="small" sx={{ color: C.forest }} />}>
        <Typography sx={{ fontSize: 13 }}>
          Three report sets for <b>{fy}</b>. Each opens as a branded, print-ready PDF. Switch the
          year in the top bar to regenerate for a prior period.
        </Typography>
      </Banner>

      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <DownloadCard
            stacked
            tag="HR" tagColor={C.forest}
            title="HR Workforce Health Report"
            sub={`${fy} · internal`}
            blurb="Exec summary · KPI & participation · risk stratification · biomarker concerns · department & programme analytics · year-on-year."
            onClick={runHrPDF}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <DownloadCard
            stacked
            tag="CP" tagColor={C.goldD}
            title="Statutory Compliance Report"
            sub={`${fy} · inspector-ready`}
            blurb="Compliance score · full mandate map · surveillance methodology · open-items CAPA · Certifying-Surgeon declaration."
            onClick={runStatutoryPDF}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <DownloadCard
            stacked
            tag="ESG" tagColor={C.forestXL}
            title="ESG & BRSR Health Disclosure"
            sub={`${fy} · GRI 403 · BRSR P-3`}
            blurb="GRI 403 occupational-health indicators · BRSR Principle-3 well-being metrics · coverage, promotion & surveillance disclosures."
            onClick={runEsgPDF}
          />
        </Grid>
      </Grid>

      <Card sx={{ mt: 2.25 }}>
        <CardHead title="Report contents at a glance" />
        <DataTable
          head={[
            'Section',
            { label: 'HR', align: 'c' },
            { label: 'Statutory', align: 'c' },
            { label: 'ESG / BRSR', align: 'c' },
          ]}
          rows={matrix.map((r) => [
            { node: <span style={{ fontWeight: 600 }}>{r[0]}</span> },
            r[1], r[2], r[3],
          ])}
        />
      </Card>
    </Box>
  );
};

const Settings = ({ ctx }) => {
  const {
    config, setConfig, mandates, setMandates,
    applyPreset, saveSettings, onClientLogo, clearClientLogo,
    saving,
  } = ctx;
  const activeCount = MANDATES.filter((m) => mandates[m[0]] !== false).length;
  const fileRef = useRef(null);

  const updateField = (k) => (e) => setConfig({ ...config, [k]: e.target.value });

  return (
    <Box>
      <Banner kind="info" icon={<SettingsOutlinedIcon fontSize="small" sx={{ color: C.forest }} />}>
        <Typography sx={{ fontSize: 13 }}>
          Customise this corporate client — branding for co-branded reports, the identity used on
          letterheads &amp; declarations, and the exact set of statutory mandates that apply to
          their operations.
        </Typography>
      </Banner>

      <Card>
        <CardHead title="Client Branding" note="co-brands the portal & all PDFs" />
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2.25}
          alignItems={{ sm: 'center' }}
          sx={{
            border: `1.5px dashed ${C.lineD}`, borderRadius: '12px',
            p: 2.25, bgcolor: C.wash,
          }}
        >
          <Box sx={{
            width: 160, height: 74, borderRadius: '9px', bgcolor: '#fff',
            border: `1px solid ${C.line}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0, overflow: 'hidden',
          }}>
            {config.clientLogo ? (
              <img src={config.clientLogo} alt="client" style={{ maxWidth: 148, maxHeight: 62, objectFit: 'contain' }} />
            ) : (
              <Stack direction="row" alignItems="center" spacing={0.75} sx={{ color: C.slateL, fontSize: 11 }}>
                <ImageOutlinedIcon sx={{ fontSize: 18 }} />
                <span>No logo</span>
              </Stack>
            )}
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: C.forest }}>
              Corporate client logo
            </Typography>
            <Typography sx={{ fontSize: 11.5, color: C.slate, m: '3px 0 9px' }}>
              PNG or JPG, ideally on a white or transparent background. Shown in the sidebar and
              co-branded onto every generated PDF (HR, Statutory &amp; ESG).
            </Typography>
            <Stack direction="row" spacing={1}>
              <Button
                onClick={() => fileRef.current && fileRef.current.click()}
                sx={{
                  fontSize: 12, fontWeight: 700, color: '#fff', bgcolor: C.forest,
                  borderRadius: '8px', px: 1.85, py: 1.1, textTransform: 'none',
                  '&:hover': { bgcolor: C.forestD },
                }}
              >
                Upload logo
              </Button>
              {config.clientLogo && (
                <Button
                  onClick={clearClientLogo}
                  sx={{
                    fontSize: 12, fontWeight: 700, color: C.slate,
                    bgcolor: C.wash2, border: `1px solid ${C.lineD}`,
                    borderRadius: '8px', px: 1.85, py: 1.1, textTransform: 'none',
                    '&:hover': { bgcolor: C.dueBg, color: C.due, borderColor: C.due },
                  }}
                >
                  Remove
                </Button>
              )}
              <input
                ref={fileRef} type="file" accept="image/png,image/jpeg" hidden
                onChange={onClientLogo}
              />
            </Stack>
          </Box>
        </Stack>
      </Card>

      <Card sx={{ mt: 2.25 }}>
        <CardHead title="Client Profile" note="used on report letterheads & the certification block" />
        <Box sx={{
          display: 'grid', gap: 1.75,
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
        }}>
          <SettingField label="Establishment / client name" value={config.clientName} onChange={updateField('clientName')} full />
          <SettingField
            label="Corporate ID"
            value={config.corpId}
            onChange={() => {}}
            readOnly
            helperText="Issued at onboarding · used as the join key across IT Dose, reports and every workspace page — cannot be changed here."
          />
          <SettingField label="Number of sites" value={config.sites} onChange={updateField('sites')} />
          <SettingField label="On-site OHCs" value={config.ohc} onChange={updateField('ohc')} />
          <SettingField label="Certifying Surgeon — name" value={config.surgeon} onChange={updateField('surgeon')} />
          <SettingField label="Certifying Surgeon — registration no." value={config.surgeonReg} onChange={updateField('surgeonReg')} />
          <SettingField label="Authorised Signatory — name" value={config.signatory} onChange={updateField('signatory')} />
          <SettingField label="Authorised Signatory — designation" value={config.signatoryDesig} onChange={updateField('signatoryDesig')} />
        </Box>
      </Card>

      <Card sx={{ mt: 2.25 }}>
        <CardHead
          title="Applicable Occupational Statutory Compliances"
          note={`${activeCount} of ${MANDATES.length} active`}
        />
        <Typography sx={{ fontSize: 12, color: C.slate, mb: 1 }}>
          Toggle exactly what applies to this client. Deselected mandates are removed from the
          statutory register, the compliance &amp; ESG reports, the inspection desk and the
          deadline engine — so an office-only client need not carry hazardous-process or mining
          requirements.
        </Typography>
        <Stack direction="row" flexWrap="wrap" alignItems="center" gap={1} sx={{ mt: 0.25, mb: 1.75 }}>
          <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: C.slate, mr: 0.25 }}>
            Industry preset:
          </Typography>
          {Object.entries(PRESET_LABELS).map(([k, label]) => (
            <Button
              key={k}
              onClick={() => applyPreset(k)}
              sx={{
                fontSize: 11.5, fontWeight: 600, color: C.forest,
                bgcolor: C.wash2, border: `1px solid ${C.lineD}`,
                borderRadius: '100px', px: 1.6, py: 0.75,
                textTransform: 'none', minWidth: 0,
                '&:hover': { bgcolor: C.forest, color: '#fff', borderColor: C.forest },
              }}
            >
              {label}
            </Button>
          ))}
        </Stack>

        <Box>
          {MANDATES.map((m) => (
            <Box
              key={m[0]}
              sx={{
                display: 'grid', gridTemplateColumns: '1fr auto', gap: 1.75,
                alignItems: 'center', py: 1.5,
                borderBottom: `1px solid ${C.line}`,
                '&:last-child': { borderBottom: 'none' },
              }}
            >
              <Box>
                <Typography sx={{ fontWeight: 600, fontSize: 13, color: C.ink }}>{m[1]}</Typography>
                <Typography sx={{ fontSize: 11, color: C.slateL, mt: '1px' }}>{m[2]}</Typography>
              </Box>
              <Switch
                checked={mandates[m[0]] !== false}
                onChange={(e) => setMandates({ ...mandates, [m[0]]: e.target.checked })}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': { color: '#fff' },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: C.forestXL, opacity: 1 },
                }}
              />
            </Box>
          ))}
        </Box>
      </Card>

      <Stack direction="row" alignItems="center" spacing={1.75} sx={{ mt: 2.5 }}>
        <Button
          onClick={saveSettings}
          disabled={saving}
          sx={{
            fontSize: 14, fontWeight: 700, color: '#fff', bgcolor: C.goldD,
            borderRadius: '9px', px: 3.25, py: 1.5, textTransform: 'none',
            '&:hover':    { bgcolor: C.gold },
            '&.Mui-disabled': { bgcolor: C.lineD, color: '#fff' },
          }}
        >
          {saving ? 'Saving…' : 'Save settings'}
        </Button>
        <Typography sx={{ fontSize: 11.5, color: C.slate }}>
          Applies immediately across the portal &amp; report generation.
        </Typography>
      </Stack>

      <FootNote>
        In production these settings persist per corporate client and govern report content, renewal
        scheduling and access. (Demo build: held in memory for this session — uploaded logos and
        edits reset on reload.)
      </FootNote>
    </Box>
  );
};

const SettingField = ({ label, value, onChange, full, readOnly, helperText }) => (
  <Box sx={{ gridColumn: full ? { md: '1 / -1' } : 'auto' }}>
    <Typography sx={{ fontSize: 11.5, fontWeight: 600, color: C.forest, mb: 0.75 }}>
      {label}
    </Typography>
    <TextField
      value={value == null ? '' : value}
      onChange={readOnly ? undefined : onChange}
      fullWidth
      size="small"
      InputProps={{ readOnly: !!readOnly }}
      sx={{
        '& .MuiOutlinedInput-root': {
          fontSize: 13.5,
          // Read-only fields render on a deeper wash so the user can
          // tell at a glance they're not editable. Cursor stays default
          // (not text-caret) so the affordance is obvious.
          bgcolor: readOnly ? C.wash2 : C.wash,
          cursor: readOnly ? 'default' : 'text',
          '& fieldset': { borderColor: C.lineD, borderWidth: '1.5px' },
          '&:hover fieldset': { borderColor: C.lineD },
          '&.Mui-focused fieldset': { borderColor: readOnly ? C.lineD : C.forestXL },
          '&.Mui-focused': { bgcolor: readOnly ? C.wash2 : '#fff' },
          borderRadius: '8px',
        },
        '& input': {
          padding: '10px 12px',
          color: readOnly ? C.slate : C.ink,
          cursor: readOnly ? 'default' : 'text',
        },
      }}
    />
    {helperText && (
      <Typography sx={{ mt: 0.5, fontSize: 10.5, color: C.slateL, lineHeight: 1.45 }}>
        {helperText}
      </Typography>
    )}
  </Box>
);

// ═════════════════════════════════════════════════════════════════
// Sidebar nav config
// ═════════════════════════════════════════════════════════════════
const SECTIONS = [
  { key: 'overview',   label: 'Overview',                icon: <DashboardOutlinedIcon fontSize="small" />,         group: 'Workspace' },
  { key: 'workforce',  label: 'Workforce Reports',       icon: <PeopleAltOutlinedIcon fontSize="small" />,         group: 'Workspace' },
  { key: 'analytics',  label: 'Health Analytics',        icon: <InsightsOutlinedIcon fontSize="small" />,          group: 'Workspace' },
  { key: 'hps',        label: 'Progressive HPS',         icon: <MonitorHeartOutlinedIcon fontSize="small" />,      group: 'Workspace' },
  { key: 'rounds',     label: 'Checkup Rounds',          icon: <LayersOutlinedIcon fontSize="small" />,            group: 'Workspace' },
  { key: 'statutory',  label: 'Statutory Compliance',    icon: <VerifiedOutlinedIcon fontSize="small" />,          group: 'Compliance' },
  { key: 'alerts',     label: 'Compliance Alerts',       icon: <NotificationsActiveOutlinedIcon fontSize="small" />, group: 'Compliance' },
  { key: 'esg',        label: 'ESG Disclosure',          icon: <EnergySavingsLeafOutlinedIcon fontSize="small" />, group: 'Compliance' },
  { key: 'inspection', label: 'Inspection Desk',         icon: <WorkspacePremiumOutlinedIcon fontSize="small" />,  group: 'Compliance' },
  { key: 'reports',    label: 'Reports & Downloads',     icon: <ArticleOutlinedIcon fontSize="small" />,           group: 'Compliance' },
  { key: 'settings',   label: 'Settings',                icon: <SettingsOutlinedIcon fontSize="small" />,          group: 'Admin' },
];

const TITLES = {
  overview:   ['Workspace',  'Corporate Health Overview'],
  workforce:  ['Workspace',  'Workforce Reports'],
  analytics:  ['Workspace',  'Workforce Health Analytics'],
  hps:        ['Workspace',  'Health Progressive Intelligence'],
  rounds:     ['Workspace',  'Checkup Rounds'],
  statutory:  ['Compliance', 'Statutory Compliance Register'],
  alerts:     ['Compliance', 'Compliance Deadline Engine'],
  esg:        ['Compliance', 'ESG & BRSR Health Disclosure'],
  inspection: ['Compliance', 'Inspection Desk'],
  reports:    ['Compliance', 'Reports & Downloads'],
  settings:   ['Admin',      'Client Settings & Customisation'],
};

// ═════════════════════════════════════════════════════════════════
// Root
// ═════════════════════════════════════════════════════════════════
const CorporateDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [section, setSection] = useState('overview');
  const [fy, setFy] = useState('FY 2025-26');
  const [round, setRound] = useState('ALL');
  // Server-hydrated drafts. The Settings page reads & mutates these
  // locally for snappy editing; pressing Save flushes the whole draft
  // to /api/portal/corp/settings via the mutation below. RTK's tag
  // invalidation refetches the canonical copy after save.
  const { data: serverSettings } = useGetCorpSettingsQuery();
  const [updateCorpSettings, { isLoading: saving }] = useUpdateCorpSettingsMutation();

  const [mandates, setMandates] = useState(MANDATE_DEFAULT);
  const [config, setConfig] = useState({
    clientName: CLIENT_DEFAULTS.name, corpId: CLIENT_DEFAULTS.id,
    sites: CLIENT_DEFAULTS.sites, ohc: CLIENT_DEFAULTS.ohc,
    surgeon: '', surgeonReg: '', signatory: '', signatoryDesig: '',
    clientLogo: null, clientLogoFmt: 'PNG', clientLogoAR: 2.6,
  });
  const [toast, setToast] = useState({ open: false, msg: '' });
  // TrustLab logo, pre-decoded once for fast PDF embedding.
  const [brandAsset, setBrandAsset] = useState(null);

  const showToast = (msg) => setToast({ open: true, msg });

  // Hydrate the local draft whenever the server response changes —
  // first load, after Save (tag invalidation), or token refresh.
  useEffect(() => {
    if (!serverSettings) return;
    setConfig({
      clientName:     serverSettings.clientName     || '',
      corpId:         serverSettings.corpId         || '',
      sites:          serverSettings.sites          ?? 1,
      ohc:            serverSettings.ohc            ?? 0,
      surgeon:        serverSettings.surgeon        || '',
      surgeonReg:     serverSettings.surgeonReg     || '',
      signatory:      serverSettings.signatory      || '',
      signatoryDesig: serverSettings.signatoryDesig || '',
      clientLogo:     serverSettings.clientLogo     || null,
      clientLogoFmt:  serverSettings.clientLogoFmt  || 'PNG',
      clientLogoAR:   serverSettings.clientLogoAR   || 2.6,
    });
    setMandates({ ...MANDATE_DEFAULT, ...(serverSettings.mandates || {}) });
  }, [serverSettings]);

  useEffect(() => {
    loadPortalFont();
    document.body.classList.add(BODY_CLASS);
    return () => {
      document.body.classList.remove(BODY_CLASS);
    };
  }, []);

  // Load the TrustLab PNG once and stash it as a data URL so the PDF
  // masthead can embed it synchronously inside addImage().
  useEffect(() => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        canvas.getContext('2d').drawImage(img, 0, 0);
        setBrandAsset({ dataUrl: canvas.toDataURL('image/png'), ar: img.naturalWidth / img.naturalHeight });
      } catch (_e) { /* canvas tainted; PDFs fall back to text mark */ }
    };
    img.src = logoSrc;
  }, []);

  // Reset round when the FY changes — its options switch too.
  useEffect(() => { setRound('ALL'); }, [fy]);

  // Topbar round selector reads the live rounds list so newly-added
  // rounds show up immediately. Falls back to the hardcoded demo
  // set while a fresh client has nothing logged yet so the dropdown
  // is never empty.
  const { data: topbarLiveRounds = [] } = useListRoundsQuery(fy);
  const topbarRounds = topbarLiveRounds.length > 0
    ? topbarLiveRounds.map((r) => ({ code: r.code, name: r.name }))
    : (DATA[fy]?.rounds || []).map((r) => ({ code: r[0], name: r[1] }));

  const mOn = (id) => mandates[id] !== false;

  const applyPreset = (k) => {
    const p = PRESETS[k];
    if (!p) return;
    const next = { ...mandates };
    Object.keys(p).forEach((id) => { next[id] = !!p[id]; });
    next.bmw = true;
    next.dpdp = true;
    setMandates(next);
    showToast(`${PRESET_LABELS[k]} preset applied — review toggles & Save`);
  };
  const saveSettings = async () => {
    try {
      await updateCorpSettings({ ...config, mandates }).unwrap();
      showToast('Settings saved — applied across portal & reports');
    } catch (e) {
      showToast(e?.data?.message || 'Could not save settings');
    }
  };
  const onClientLogo = (e) => {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const url = ev.target.result;
      const fmt = String(url).includes('image/png') ? 'PNG' : 'JPEG';
      const im = new Image();
      im.onload = () => {
        setConfig((prev) => ({
          ...prev, clientLogo: url, clientLogoFmt: fmt, clientLogoAR: im.width / im.height,
        }));
      };
      im.src = url;
      showToast('Client logo updated');
    };
    reader.readAsDataURL(f);
  };
  const clearClientLogo = () => {
    setConfig((prev) => ({ ...prev, clientLogo: null }));
    showToast('Client logo removed');
  };

  // ─── PDF generators ─────────────────────────────────────────
  const pdfClient = () => ({
    name: config.clientName,
    logo: config.clientLogo,
    logoFmt: config.clientLogoFmt,
    logoAR: config.clientLogoAR,
    sites: config.sites,
    ohc: config.ohc,
  });

  const runHrPDF = () => {
    const d = DATA[fy];
    const doc = brandPDF('HR Workforce Health Report', `${config.clientName}  ·  ${fy}`, pdfClient(), brandAsset);
    let y = 116;
    doc.setTextColor(120, 130, 124); doc.setFontSize(8);
    doc.text(
      `Corporate ID ${config.corpId}   ·   Generated ${new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}   ·   ${d.rounds.length} checkup rounds   ·   Confidential — internal use`,
      40, y,
    );
    y += 18;

    y = secTitle(doc, '1.  Executive Summary', y);
    y = paraPDF(doc, `${fmt(d.screened)} of ${fmt(d.eligible)} eligible employees were screened in ${fy} (${d.participation}% participation) across ${d.rounds.length} checkup rounds. ${fmt(d.highRisk)} employees were flagged as high-risk and ${d.criticalRefer} received urgent clinical referral. The corporate compliance score stands at ${d.complianceScore}/100.`, y);
    y = tablePDF(doc, ['Indicator', 'Value', 'Indicator', 'Value'], [
      ['Workforce',     fmt(d.workforce),    'Compliance score',     `${d.complianceScore} / 100`],
      ['Eligible',      fmt(d.eligible),     'High-risk employees',  fmt(d.highRisk)],
      ['Screened',      fmt(d.screened),     'Urgent referrals',     String(d.criticalRefer)],
      ['Participation', `${d.participation}%`, 'Open statutory items', String(d.openItems)],
    ], y);

    y = secTitle(doc, '2.  Health Risk Stratification', y);
    y = tablePDF(doc, ['Risk band', 'Employees', 'Share'],
      d.risk.map((r) => [r[0], fmt(r[1]), `${r[2]}%`]), y,
      { col: { 1: { halign: 'center' }, 2: { halign: 'center' } } });

    y = secTitle(doc, '3.  Top Biomarker Concerns', y);
    y = tablePDF(doc, ['Biomarker / finding', 'Prevalence (% of screened)'],
      d.biomarkers.map((b) => [b[0], `${b[1]}%`]), y,
      { col: { 1: { halign: 'center' } } });

    y = secTitle(doc, '4.  Department Health Distribution', y);
    y = tablePDF(doc, ['Department', 'Screened', 'Elevated-risk share'],
      d.departments.map((x) => [x[0], fmt(x[1]), `${x[2]}%`]), y,
      { col: { 1: { halign: 'center' }, 2: { halign: 'center' } } });

    y = secTitle(doc, '5.  Programme Participation & Turnaround', y);
    y = tablePDF(doc, ['Programme', 'Eligible', 'Completed', 'Participation', 'Avg TAT'],
      d.programmes.map((p) => [p[0], fmt(p[1]), fmt(p[2]), `${p[3]}%`, `${p[4]}h`]), y,
      { col: { 1: { halign: 'center' }, 2: { halign: 'center' }, 3: { halign: 'center' }, 4: { halign: 'center' } } });

    if (fy === 'FY 2025-26') {
      const p = DATA['FY 2024-25'];
      y = secTitle(doc, '6.  Year-on-Year Movement', y);
      y = tablePDF(doc, ['Metric', 'FY 2024-25', 'FY 2025-26', 'Movement'], [
        ['Participation',         `${p.participation}%`, `${d.participation}%`, `+${d.participation - p.participation} pts`],
        ['Compliance score',      `${p.complianceScore}/100`, `${d.complianceScore}/100`, `+${d.complianceScore - p.complianceScore}`],
        ['Pre-diabetic share',    `${p.biomarkers[1][1]}%`,  `${d.biomarkers[1][1]}%`,  `${d.biomarkers[1][1] - p.biomarkers[1][1]} pts`],
        ['Vitamin D deficiency',  `${p.biomarkers[0][1]}%`,  `${d.biomarkers[0][1]}%`,  `${d.biomarkers[0][1] - p.biomarkers[0][1]} pts`],
        ['Avg AHC turnaround',    `${p.programmes[0][4]}h`,  `${d.programmes[0][4]}h`,  `${d.programmes[0][4] - p.programmes[0][4]}h`],
      ], y, { col: { 1: { halign: 'center' }, 2: { halign: 'center' }, 3: { halign: 'center' } } });
    }

    y = paraPDF(doc, `Prepared by TrustLab Diagnostics Pvt Ltd under NABL ISO 15189:2022 accreditation. Contains aggregate occupational health data processed under the DPDP Act, 2023; restricted to authorised HR and leadership personnel of ${config.clientName}.`, y + 4);
    footerPDF(doc);
    doc.save(`TrustLab-HR-Health-Report-${fy.replace(/\s/g, '')}.pdf`);
    showToast('HR Workforce Health Report downloaded');
  };

  const runStatutoryPDF = () => {
    const d = DATA[fy];
    const doc = brandPDF('Statutory Compliance Report', `${config.clientName}  ·  ${fy}`, pdfClient(), brandAsset);
    let y = 116;
    doc.setTextColor(120, 130, 124); doc.setFontSize(8);
    doc.text(`Ref TL-OHC-CP-001   ·   Generated ${new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}   ·   Occupational Health Statutory Compliance Pack   ·   Inspector-ready`, 40, y);
    y += 18;

    const active = activeStat(d, mOn);

    y = secTitle(doc, 'A.  Compliance Summary', y);
    y = paraPDF(doc, `Compliance score ${d.complianceScore}/100. ${d.openItems === 0
      ? 'All statutory medical obligations fully discharged for the period.'
      : `${d.openItems} individual items remain open across the period, each within its permissible statutory window and tracked in the action register (Section D).`} Last external inspection cleared on ${d.lastInspection}; last ESIC inspection cleared on ${d.lastESIC}.`, y);
    y = tablePDF(doc, ['Statutory category', 'Reference', 'Mandated', 'Done', 'Status', 'Reviewed'],
      active.map((s) => [s[0], s[1], String(s[2]), String(s[3]), s[5], s[6]]), y,
      { col: { 2: { halign: 'center' }, 3: { halign: 'center' }, 4: { halign: 'center' } } });

    y = secTitle(doc, 'B.  Statutory Mandate Reference Map', y);
    y = paraPDF(doc, 'Each obligation an inspector may test, mapped to its governing statute and the register, form or report that discharges it.', y);
    y = tablePDF(doc, ['Governing statute / reference', 'Obligation', 'Applies', 'How discharged'],
      MANDATE_MAP.map((m) => {
        const id = mapRowId(m[0]);
        const on = id ? mOn(id) : true;
        const apTxt = on ? (m[0].includes('Bio-Medical') ? 'Service-side' : 'Applicable') : 'Not applicable';
        const how = id && !on ? 'Deselected for this client (Settings) — excluded.' : m[4];
        return [`${m[0]}\n${m[1]}`, m[2], apTxt, how];
      }), y,
      { col: { 0: { cellWidth: 120 }, 2: { cellWidth: 64, halign: 'center' } } });

    y = secTitle(doc, 'C.  Surveillance Methodology & Quality Basis', y);
    y = tablePDF(doc, ['Surveillance / basis', 'Standard', 'Coverage'], [
      ['Audiometric surveillance (noise-exposed)', 'ISO 1999:2013', `${d.statutory[4][3]} of ${d.statutory[4][2]}`],
      ['Spirometric surveillance (dust-exposed)',  'ATS/ERS 2019', `${d.statutory[5][3]} of ${d.statutory[5][2]}`],
      ['Laboratory analytical quality',            'NABL ISO 15189:2022', 'All testing'],
      ['Health-data protection',                   'DPDP Act, 2023', 'Segregated consent'],
    ], y, { col: { 2: { halign: 'center' } } });

    y = secTitle(doc, 'D.  Open Items & Action Register (CAPA)', y);
    if (d.capa.length) {
      y = tablePDF(doc, ['Open item', 'Count', 'Closure window', 'Owner'],
        d.capa.map((x) => [x[0], String(x[1]), x[2], x[3]]), y,
        { col: { 1: { halign: 'center' } } });
      y = paraPDF(doc, 'No item is overdue against its statutory window as at the generation date.', y);
    } else {
      y = paraPDF(doc, 'No open items. All statutory obligations fully discharged for the period.', y);
    }

    y = secTitle(doc, 'E.  Declaration & Certification', y);
    y = paraPDF(doc, `This report consolidates the medical-examination and surveillance records delivered by TrustLab Diagnostics Pvt Ltd to ${config.clientName} for ${fy}, mapped to the applicable Indian statutory obligations. Examinations were performed by registered medical practitioners and certified, where required, by the establishment's Certifying Surgeon; laboratory testing was performed under live NABL ISO 15189:2022 accreditation. Aggregate figures reconcile to the underlying Form 17 registers, Form 33 / Form 1A certificates and surveillance reports retained at each site. This report indexes the primary evidentiary records; it does not replace them.`, y);
    if (y > doc.internal.pageSize.getHeight() - 120) { doc.addPage(); y = 56; }
    y += 6;
    doc.setDrawColor(40, 50, 45); doc.setLineWidth(1);
    doc.line(40, y + 34, 250, y + 34);
    doc.line(310, y + 34, 520, y + 34);
    doc.setTextColor(26, 58, 42); doc.setFont('helvetica', 'bold'); doc.setFontSize(9.5);
    doc.text('Certifying Surgeon', 40, y + 46);
    doc.text('Authorised Signatory', 310, y + 46);
    doc.setFont('helvetica', 'normal'); doc.setTextColor(90, 104, 98); doc.setFontSize(8);
    doc.text(`For ${config.clientName}`, 40, y + 57);
    doc.text('For TrustLab Diagnostics Pvt Ltd', 310, y + 57);
    doc.text(config.surgeon
      ? `${config.surgeon}${config.surgeonReg ? '  ·  Reg. ' + config.surgeonReg : ''}`
      : 'Name · Reg. No. · Date · Seal', 40, y + 68);
    doc.text(config.signatory
      ? `${config.signatory}${config.signatoryDesig ? '  ·  ' + config.signatoryDesig : ''}`
      : 'Name · Designation · Date · Seal', 310, y + 68);

    footerPDF(doc);
    doc.save(`TrustLab-Statutory-Compliance-Report-${fy.replace(/\s/g, '')}.pdf`);
    showToast('Statutory Compliance Report downloaded');
  };

  const runEsgPDF = () => {
    const d = DATA[fy];
    const e = EXT[fy];
    const doc = brandPDF('ESG & BRSR Health Disclosure', `${config.clientName}  ·  ${fy}`, pdfClient(), brandAsset);
    let y = 116;
    doc.setTextColor(120, 130, 124); doc.setFontSize(8);
    doc.text(`GRI 403 · BRSR Principle 3   ·   Generated ${new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}   ·   Sustainability-report ready`, 40, y);
    y += 18;

    y = secTitle(doc, '1.  Scope & Basis', y);
    y = paraPDF(doc, `This disclosure maps occupational-health and well-being data delivered by TrustLab Diagnostics Pvt Ltd to ${config.clientName} for ${fy} against the GRI 403 Occupational Health & Safety (2018) Standard and SEBI Business Responsibility & Sustainability Reporting (BRSR) Principle 3.`, y);

    y = secTitle(doc, '2.  GRI 403 — Occupational Health & Safety', y);
    y = tablePDF(doc, ['Disclosure', 'Indicator', 'Position'], [
      ['GRI 403-1', 'OH&S management system', 'Maintained by the occupier; medical surveillance delivered by TrustLab under NABL ISO 15189:2022 accreditation.'],
      ['GRI 403-3', 'Occupational health services', `Pre-employment, periodic & statutory medical examinations across ${config.sites} sites with ${config.ohc} on-site OHC.`],
      ['GRI 403-5', 'Worker training / advisory on health', 'Post-screening individual health advisories issued to all screened workers; counselling for high-risk cases.'],
      ['GRI 403-6', 'Promotion of worker health', `AHC (${d.participation}% participation), Executive Health Programme, vaccination, audiometry/spirometry surveillance, stress & burnout screen.`],
      ['GRI 403-9', 'Work-related injuries', 'Injury register maintained by occupier; pre-employment fitness & return-to-work assessments supported by TrustLab.'],
      ['GRI 403-10', 'Work-related ill health', `Surveillance for noise-induced hearing loss (ISO 1999:2013) and dust-related impairment (ATS/ERS 2019); ${d.criticalRefer} urgent clinical referrals this period.`],
    ], y, { col: { 0: { cellWidth: 58 }, 1: { cellWidth: 120 } } });

    y = secTitle(doc, '3.  BRSR Principle 3 — Employee Well-being', y);
    y = tablePDF(doc, ['Indicator', 'Value'], [
      ['Workers covered by occupational health services', '100% of eligible workforce'],
      ['Annual health screening participation', `${d.participation}% (${fmt(d.screened)} of ${fmt(d.eligible)})`],
      ['Statutory medical compliance score', `${d.complianceScore} / 100`],
      ['Health & well-being measures', 'AHC · Executive Health · Vaccination · Audiometry/Spirometry surveillance · Stress & Burnout screen'],
      ['High-risk employees identified & referred', `${fmt(d.highRisk)} flagged · ${d.criticalRefer} urgent referrals`],
      ['Progressive health intelligence', `Workforce HPS ${hpsScore(e.hps.pillars)}/900 · ${e.forecastAvoid} chronic-disease cases modelled as avoidable`],
      ['Access to non-occupational medical services', 'TrustLab 14-branch pan-India network + teleconsultation'],
    ], y, { col: { 0: { cellWidth: 210 } } });

    y = secTitle(doc, '4.  Surveillance & Quality Basis', y);
    y = tablePDF(doc, ['Surveillance / basis', 'Standard', 'Coverage'], [
      ['Audiometric surveillance (noise-exposed)', 'ISO 1999:2013', `${d.statutory[4][3]} of ${d.statutory[4][2]}`],
      ['Spirometric surveillance (dust-exposed)', 'ATS/ERS 2019', `${d.statutory[5][3]} of ${d.statutory[5][2]}`],
      ['Laboratory analytical quality', 'NABL ISO 15189:2022', 'All testing'],
      ['Health-data protection', 'DPDP Act, 2023', 'Consent-based'],
    ], y, { col: { 2: { halign: 'center' } } });

    y = secTitle(doc, '5.  Attestation', y);
    y = paraPDF(doc, `The figures herein reconcile to the occupational-health records and statutory registers retained for ${config.clientName}. Work-related injury data (GRI 403-9) is maintained by the occupier; this disclosure covers the medical-surveillance and health-promotion components delivered by TrustLab Diagnostics Pvt Ltd under NABL ISO 15189:2022 accreditation.`, y);

    footerPDF(doc);
    doc.save(`TrustLab-ESG-BRSR-Health-Disclosure-${fy.replace(/\s/g, '')}.pdf`);
    showToast('ESG & BRSR Health Disclosure downloaded');
  };

  // ─── Sidebar ─────────────────────────────────────────────────
  const grouped = SECTIONS.reduce((acc, s) => {
    (acc[s.group] = acc[s.group] || []).push(s);
    return acc;
  }, {});

  const sidebar = (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100%' }}>
      {Object.entries(grouped).map(([group, items]) => (
        <Box key={group}>
          <SidebarLabel>{group}</SidebarLabel>
          {items.map((s) => {
            const isActive = section === s.key;
            const iconEl = React.cloneElement(s.icon, {
              sx: { fontSize: 18, color: isActive ? C.forestD : 'rgba(255,255,255,0.55)' },
            });
            return (
              <SidebarNavItem
                key={s.key}
                icon={iconEl}
                label={s.label}
                active={isActive}
                activeBg={C.gold}
                activeFg={C.forestD}
                onClick={() => setSection(s.key)}
              />
            );
          })}
        </Box>
      ))}

      <Box sx={{ mt: 'auto', pt: 2 }}>
        <SidebarUser
          initials={(user?.name || user?.email || 'U').split(/[\s@.]+/).filter(Boolean).slice(0, 2).map((p) => p[0]?.toUpperCase()).join('') || 'U'}
          name={user?.name || 'HR Manager'}
          role={config.clientName}
          avatarColor={C.gold}
          avatarSrc={config.clientLogo}
        />
      </Box>
    </Box>
  );

  // Footer slot — "Back to Website" button.
  const backToSite = (
    <Button
      fullWidth
      onClick={() => navigate(ROUTES.HOME)}
      startIcon={<ArrowBackIcon />}
      sx={{
        color: 'rgba(255,255,255,0.75)',
        bgcolor: 'rgba(200,162,75,0.10)',
        border: '1px solid rgba(200,162,75,0.30)',
        borderRadius: 2, fontSize: 13, fontWeight: 700,
        textTransform: 'none', letterSpacing: '0.2px',
        transition: 'all 0.2s',
        '&:hover': { bgcolor: C.gold, borderColor: C.gold, color: C.forestD },
      }}
    >
      Back to Website
    </Button>
  );

  const [crumb, pageTitle] = TITLES[section] || ['Workspace', 'Corporate Health Overview'];

  const topbarTitleSlot = (
    <Stack
      key={section}
      direction="row"
      spacing={1.5}
      alignItems="center"
      sx={{
        minWidth: 0,
        animation: 'cp-topbar-fade 0.3s ease-out',
        '@keyframes cp-topbar-fade': {
          from: { opacity: 0, transform: 'translateX(-4px)' },
          to:   { opacity: 1, transform: 'translateX(0)' },
        },
      }}
    >
      <Box
        sx={{
          width: 44, height: 44, borderRadius: '10px', flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          bgcolor: '#fff',
          border: `1px solid ${C.line}`,
          boxShadow: '0 2px 6px rgba(15,36,25,0.06)',
          overflow: 'hidden', p: 0.5,
        }}
      >
        {config.clientLogo ? (
          <Box
            component="img"
            src={config.clientLogo}
            alt={config.clientName}
            sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
          />
        ) : (
          <Typography
            sx={{
              fontSize: 20, fontWeight: 800, color: C.forest, lineHeight: 1,
            }}
          >
            {(config.clientName || 'C').charAt(0).toUpperCase()}
          </Typography>
        )}
      </Box>

      <Box sx={{ minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: 9.5, fontWeight: 700,
            color: C.goldD, textTransform: 'uppercase',
            letterSpacing: '0.16em', lineHeight: 1,
          }}
        >
          {crumb}
        </Typography>
        <Typography
          sx={{
            fontSize: { xs: 16, md: 20 },
            fontWeight: 700, color: C.forest,
            letterSpacing: '-0.2px', lineHeight: 1.2, mt: 0.5,
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            position: 'relative',
            '&::before': {
              content: '""', position: 'absolute',
              left: -10, top: '15%', bottom: '15%',
              width: 3, borderRadius: 2,
              background: `linear-gradient(180deg, ${C.gold} 0%, ${C.goldD} 100%)`,
              display: { xs: 'none', md: 'block' },
            },
          }}
        >
          {pageTitle}
        </Typography>
      </Box>
    </Stack>
  );

  // FY / Round selectors shared by topbar and reused in views.
  const selectorSx = {
    fontSize: 13, fontWeight: 600, color: C.forest,
    bgcolor: C.wash, borderRadius: '8px',
    '& fieldset': { borderColor: C.lineD, borderWidth: '1.5px' },
    '& .MuiSelect-select': { py: '7px', pl: 1.5 },
  };

  const topbarRight = (
    <Stack direction="row" spacing={2} alignItems="center">
      <Stack direction="row" spacing={1} alignItems="center" sx={{ display: { xs: 'none', md: 'flex' } }}>
        <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: C.slate, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          Year
        </Typography>
        <Select
          value={fy}
          onChange={(e) => setFy(e.target.value)}
          size="small"
          sx={selectorSx}
        >
          {Object.keys(DATA).map((y) => (
            <MenuItem key={y} value={y}>{y}</MenuItem>
          ))}
        </Select>
      </Stack>

      <Stack direction="row" spacing={1} alignItems="center" sx={{ display: { xs: 'none', md: 'flex' } }}>
        <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: C.slate, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          Round
        </Typography>
        <Select
          value={round}
          onChange={(e) => setRound(e.target.value)}
          size="small"
          sx={selectorSx}
        >
          <MenuItem value="ALL">All rounds (annual)</MenuItem>
          {topbarRounds.map((r) => (
            <MenuItem key={r.code} value={r.code}>{r.code} · {r.name}</MenuItem>
          ))}
        </Select>
      </Stack>

      <Typography
        sx={{
          fontSize: 12, color: C.slate,
          whiteSpace: 'nowrap',
          display: { xs: 'none', lg: 'block' },
        }}
      >
        {TODAY_LABEL}
      </Typography>
    </Stack>
  );

  // Render-context handed to every section.
  const ctx = {
    fy, round, role: 'HR',
    client: { name: config.clientName, sites: config.sites, ohc: config.ohc, id: config.corpId },
    mOn,
    config, setConfig,
    mandates, setMandates,
    applyPreset, saveSettings, saving,
    onClientLogo, clearClientLogo,
    toast: showToast,
    runHrPDF, runStatutoryPDF, runEsgPDF,
  };

  const body = {
    overview:   <Overview   ctx={ctx} />,
    workforce:  <Workforce />,
    analytics:  <Analytics  ctx={ctx} />,
    hps:        <HPSView    ctx={ctx} />,
    rounds:     <Rounds     ctx={ctx} />,
    statutory:  <Statutory  ctx={ctx} />,
    alerts:     <Alerts     ctx={ctx} />,
    esg:        <ESG        ctx={ctx} />,
    inspection: <Inspection ctx={ctx} />,
    reports:    <Reports    ctx={ctx} />,
    settings:   <Settings   ctx={ctx} />,
  }[section] || <Overview ctx={ctx} />;

  return (
    <PortalLayout
      brand="TrustLab"
      brandSub="Corporate Portal"
      brandSlot={
        <Stack alignItems="center" spacing={1}>
          <Logo size="md" />
          <Typography
            sx={{
              fontSize: 9,
              letterSpacing: '2.5px',
              textTransform: 'uppercase',
              color: 'rgba(255,255,255,0.55)',
            }}
          >
            Corporate Portal
          </Typography>
        </Stack>
      }
      footerSlot={backToSite}
      accent={C.gold}
      sidebarBg={`linear-gradient(180deg, ${C.forestD} 0%, ${C.forest} 55%, ${C.forestD} 100%)`}
      topbarTitle={pageTitle}
      topbarTitleSlot={topbarTitleSlot}
      topbarRight={topbarRight}
      sidebar={sidebar}
    >
      <Box sx={{ bgcolor: C.bg, m: { xs: -2, sm: -3, md: -4 }, p: { xs: 2, sm: 3, md: 4 }, minHeight: '100%' }}>
        {body}
      </Box>

      <Snackbar
        open={toast.open}
        autoHideDuration={2600}
        onClose={() => setToast({ open: false, msg: '' })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity="success"
          icon={<CheckCircleOutlineIcon fontSize="small" />}
          sx={{
            bgcolor: C.forestD, color: '#fff',
            fontSize: 13, fontWeight: 600,
            borderRadius: '10px',
            '& .MuiAlert-icon': { color: C.goldL },
          }}
        >
          {toast.msg}
        </Alert>
      </Snackbar>
    </PortalLayout>
  );
};

export default CorporateDashboard;
